/**
 * Minimal typed API client for the Klyra backend.
 *
 * Uses fetch under the hood, adds the access token from the auth store,
 * and converts non-ok responses into thrown errors that React Query can catch.
 */
import { useAuthStore } from "../stores/auth-store.ts";

const API_URL = import.meta.env.VITE_API_URL ?? "/api/v1";

export interface ApiError {
	code: string;
	message: string;
	details?: Record<string, unknown>;
}

export class HttpError extends Error {
	constructor(
		public status: number,
		public error: ApiError,
	) {
		super(error.message);
		this.name = "HttpError";
	}
}

interface RequestOptions extends Omit<RequestInit, "body" | "headers"> {
	body?: unknown;
	headers?: Record<string, string>;
	auth?: boolean;
}

/**
 * Single-flight refresh promise so a burst of stale-token requests
 * (dashboard fires 4-5 queries in parallel on mount) all share one
 * refresh call instead of stampeding the /auth/refresh endpoint.
 */
let inflightRefresh: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
	if (inflightRefresh) return inflightRefresh;
	inflightRefresh = (async () => {
		const store = useAuthStore.getState();
		const refreshTokenValue = store.refreshToken;
		if (!refreshTokenValue) return null;
		try {
			const res = await fetch(`${API_URL}/auth/refresh`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ refreshToken: refreshTokenValue }),
			});
			const json = await res.json().catch(() => null);
			if (!res.ok || !json?.ok || !json.data?.access_token) {
				store.clearSession();
				return null;
			}
			useAuthStore.setState({
				accessToken: json.data.access_token,
				refreshToken: json.data.refresh_token ?? refreshTokenValue,
			});
			return json.data.access_token as string;
		} catch {
			return null;
		} finally {
			inflightRefresh = null;
		}
	})();
	return inflightRefresh;
}

export async function apiFetch<T>(
	path: string,
	options: RequestOptions = {},
): Promise<T> {
	const { auth = true, body, headers = {}, ...rest } = options;

	const buildRequest = (token: string | null) => {
		const finalHeaders: Record<string, string> = {
			"Content-Type": "application/json",
			...headers,
		};
		if (auth && token) finalHeaders.Authorization = `Bearer ${token}`;
		return fetch(`${API_URL}${path}`, {
			...rest,
			headers: finalHeaders,
			body: body !== undefined ? JSON.stringify(body) : undefined,
		});
	};

	const initialToken = auth ? useAuthStore.getState().accessToken : null;
	let res = await buildRequest(initialToken);

	// Transparent refresh on 401 for authenticated requests. Keeps the
	// staff dashboard live without the doctor having to log in again
	// every few hours.
	if (auth && res.status === 401) {
		const fresh = await refreshAccessToken();
		if (fresh) {
			res = await buildRequest(fresh);
		}
	}

	const json = await res.json().catch(() => null);

	if (!res.ok || !json?.ok) {
		const apiError: ApiError = json?.error ?? {
			code: "NETWORK_ERROR",
			message: `Request failed with status ${res.status}`,
		};
		throw new HttpError(res.status, apiError);
	}

	return json.data as T;
}

/**
 * Authenticated file download (CSV exports). Same Bearer + refresh-on-401
 * behavior as apiFetch, but reads the body as a Blob and triggers a browser
 * download instead of parsing JSON.
 */
export async function apiDownload(
	path: string,
	filename: string,
): Promise<void> {
	const buildRequest = (token: string | null) =>
		fetch(`${API_URL}${path}`, {
			headers: token ? { Authorization: `Bearer ${token}` } : {},
		});

	let res = await buildRequest(useAuthStore.getState().accessToken);
	if (res.status === 401) {
		const fresh = await refreshAccessToken();
		if (fresh) res = await buildRequest(fresh);
	}
	if (!res.ok) {
		const json = await res.json().catch(() => null);
		const apiError: ApiError = json?.error ?? {
			code: "NETWORK_ERROR",
			message: `Request failed with status ${res.status}`,
		};
		throw new HttpError(res.status, apiError);
	}

	const blob = await res.blob();
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
}

// ===== Typed endpoint helpers =====

import type {
	AcceptInvitationInput,
	AcceptReferralInput,
	AcceptedPrescriptionApi,
	ActionExecuteResult,
	ActionTokenInfo,
	AddAdmissionChargeInput,
	AdjustClinicSupplyInput,
	AdjustPharmacyStockInput,
	AdjustPointsInput,
	AdmissionApi,
	AdmissionBill,
	AdmissionChargeApi,
	AdmitPatientInput,
	AdvanceQueueResult,
	AestheticPlanApi,
	AmendLabValueInput,
	AnalyticsResponse,
	AnalyzeSkinInput,
	AntenatalCheckupApi,
	Appointment,
	AppointmentNoShowRisk,
	AppointmentStatus,
	AppointmentStatusAction,
	AppointmentWithPatient,
	AppointmentsListQuery,
	AuditLogsListQuery,
	AuditLogsListResult,
	AuthResponse,
	AvailableSlotsQuery,
	AvailableSlotsResponse,
	AvsInput,
	AvsResult,
	BatchMarkCriticalAlertsNotifiedInput,
	BedApi,
	BodyDrawingApi,
	BranchSummary,
	BranchesReport,
	BringInPatientResult,
	BulkImportLabTestsInput,
	CancelInvoiceInput,
	CardiacAssessmentApi,
	CheckInteractionInput,
	CheckInteractionResponse,
	ChildImmunizationApi,
	ClinicExpenseApi,
	ClinicLabPanelApi,
	ClinicLabTestApi,
	ClinicMedicationApi,
	ClinicMemberApi,
	ClinicReflexRuleApi,
	ClinicServiceApi,
	ClinicSignupInput,
	ClinicSupplyApi,
	CohortAskResult,
	CohortInsightResponse,
	CohortLocationsResponse,
	CommissionDetail,
	CommissionRangeQuery,
	CommissionSummary,
	CompareLesionInput,
	ConfirmDentalToothImageInput,
	ConfirmPhotoUploadInput,
	ConsentFormApi,
	ConsentTemplateApi,
	ConvertTreatmentItemsToInvoiceInput,
	CopilotAssessInput,
	CopilotAssessResult,
	CreateAntenatalCheckupInput,
	CreateAppointmentInput,
	CreateBedInput,
	CreateBodyDrawingInput,
	CreateCardiacAssessmentInput,
	CreateClinicSupplyInput,
	CreateConsentFormInput,
	CreateConsentTemplateInput,
	CreateCultureOrganismInput,
	CreateDentalRecallInput,
	CreateEntAssessmentInput,
	CreateExpenseInput,
	CreateEyeExamInput,
	CreateGrowthMeasurementInput,
	CreateInjectableLotInput,
	CreateInjectionRecordInput,
	CreateInpatientVitalInput,
	CreateInstrumentInput,
	CreateIntakeOutputInput,
	CreateInternalMedAssessmentInput,
	CreateInvitationInput,
	CreateInvoiceInput,
	CreateLabOrderInput,
	CreateLabPanelInput,
	CreateLabResultValuesBatchInput,
	CreateLabTestInput,
	CreateMedicationInput,
	CreateMedicationOrderInput,
	CreateNursingNoteInput,
	CreateObstetricUltrasoundInput,
	CreateOperatingRoomInput,
	CreateOrthopedicAssessmentInput,
	CreatePackageInput,
	CreatePatientInput,
	CreatePatientProblemInput,
	CreatePeriodontalExamInput,
	CreatePharmacyItemInput,
	CreatePhotoFollowupInput,
	CreatePhysicianOrderInput,
	CreatePhysioSessionInput,
	CreatePregnancyInput,
	CreatePrescriptionInput,
	CreatePrescriptionTemplateInput,
	CreatePsychAssessmentInput,
	CreatePurchaseOrderInput,
	CreateQcMaterialInput,
	CreateReagentCatalogInput,
	CreateReagentLotInput,
	CreateRecallInput,
	CreateRecurringExpenseInput,
	CreateRecurringInput,
	CreateRecurringResult,
	CreateReferralDoctorInput,
	CreateReflexRuleInput,
	CreateSaleInput,
	CreateScheduleOverrideInput,
	CreateServiceInput,
	CreateSkinLesionInput,
	CreateSkinLesionMeasurementInput,
	CreateSubscriptionRequestInput,
	CreateSupplierInput,
	CreateTaskInput,
	CreateToothConditionInput,
	CreateTreatmentPlanItemInput,
	CreateTreatmentPlanMapInput,
	CreateVisitAddendumInput,
	CreateVisitInput,
	CreateVisitTemplateInput,
	CreateVitalsInput,
	CreateWaitlistInput,
	CreateWardInput,
	CriticalAlertApi,
	CultureBundleApi,
	CultureOrganismApi,
	CurrentUserPermissions,
	DeltaCheckInput,
	DeltaCheckResponse,
	DentalChartApi,
	DentalRecallApi,
	DentalToothImageApi,
	DentalToothImagePresignInput,
	DentalToothImagePresignResponse,
	DeviceProfileKey,
	DiagnosisCode,
	DiagnosisEpiResponse,
	DiagnosisReportResponse,
	DischargeAdmissionInput,
	DoctorScheduleApi,
	EarnPointsInput,
	EdVisitApi,
	EncounterSynthesisInput,
	EntAssessmentApi,
	ExtractLabValuesInput,
	ExtractLabValuesResult,
	EyeExamApi,
	FacilityRevenueApi,
	FinanceReportQuery,
	FinanceReportResponse,
	GenerateAccessTokenResponse,
	GenerateAestheticPlanInput,
	GrowthMeasurementApi,
	HomeCollectionApi,
	IcdSuggestInput,
	IcdSuggestResult,
	ImportPatientRow,
	ImportPatientsResult,
	IncomingLabOrderApi,
	IncomingPrescriptionApi,
	InjectableLotApi,
	InjectableRecallApi,
	InjectionRecordApi,
	InpatientVitalApi,
	InsightsResponse,
	InstrumentApi,
	InstrumentTestMappingApi,
	IntakeOutputApi,
	IntakeOutputList,
	InternalMedAssessmentApi,
	InvitationApi,
	InvoiceApi,
	InvoicesListQuery,
	LabCollectionStatus,
	LabCompletionReadiness,
	LabComponentApi,
	LabOrderApi,
	LabOrderStatus,
	LabResultApi,
	LabResultValueApi,
	LabReviewResult,
	LabSafetySeedResult,
	LabValueAmendmentApi,
	LabWorklistItem,
	LesionComparisonApi,
	LinkedLabApi,
	ListCriticalAlertsQuery,
	ListSalaryPaymentsQuery,
	ListScheduleOverridesQuery,
	ListTasksQuery,
	LoginInput,
	LoyaltySettingsApi,
	LoyaltySummaryApi,
	LoyaltyTransactionApi,
	MarkCriticalAlertNotifiedInput,
	MedicationAdministrationApi,
	MedicationOrderApi,
	MemberPermissionsApi,
	NotificationApi,
	NotificationsListResponse,
	NursingNoteApi,
	ObstetricUltrasoundApi,
	OnboardingStatusApi,
	OperatingRoomApi,
	OrganismSensitivityApi,
	OrthopedicAssessmentApi,
	OutstandingReferralRow,
	PaginatedResponse,
	Patient,
	PatientDocumentApi,
	PatientIntelligenceResponse,
	PatientPackageApi,
	PatientPhotoApi,
	PatientProblemApi,
	PatientRecallApi,
	PatientVisitApi,
	PatientVitalsApi,
	PatientsListQuery,
	PaySalaryInput,
	PaymentApi,
	PendingResultsCount,
	PeriodontalExamApi,
	PharmacyItemApi,
	PharmacyPurchaseOrderApi,
	PharmacySaleApi,
	PharmacySalesReportApi,
	PharmacySupplierApi,
	PhotoFollowupApi,
	PhysicianOrderApi,
	PhysioSessionApi,
	PlatformBankDetails,
	PortalAuthResponse,
	PortalBookingInput,
	PortalLabResult,
	PortalPatientApi,
	PortalRadiologyReport,
	PortalServiceOption,
	PracticeIntelligenceResponse,
	PreVisitIntake,
	PregnancyApi,
	PrepareReminderResult,
	PrescriptionApi,
	PrescriptionLookupResult,
	PrescriptionTemplateApi,
	PresignedUploadResponse,
	PresignedViewResponse,
	PsychAssessmentApi,
	PublicAvailabilityDaysResponse,
	PublicBookingInput,
	PublicBookingResult,
	PublicClinicInfo,
	PublicClinicService,
	PublicInvitationApi,
	PublicLabOrderApi,
	PublicPrescriptionApi,
	PublicReceiptApi,
	QcChartQuery,
	QcChartResponse,
	QcMaterialApi,
	QcRunApi,
	QcTargetApi,
	QueueStateResponse,
	ReagentCatalogApi,
	ReagentConsumptionApi,
	ReagentExpiringRow,
	ReagentLotApi,
	ReagentLowStockRow,
	RecallsListQuery,
	RecordAdministrationInput,
	RecordAstInput,
	RecordImmunizationInput,
	RecordIncomingResultsInput,
	RecordPaymentInput,
	RecordQcRunsInput,
	RecordReagentConsumptionInput,
	RecurringExpenseApi,
	RedeemPointsInput,
	RedeemSessionInput,
	ReferralDoctorApi,
	ReferralDuesDetailRow,
	ReferralDuesRow,
	ReferralPartnerApi,
	ReferralPartnerDuesDetailRow,
	ReferralPartnerDuesRow,
	RefundPaymentInput,
	RegisterEdVisitInput,
	RejectLabOrderInput,
	ReminderQueueItem,
	ReminderTemplatesResponse,
	ReorderSuggestionsResponse,
	RequestDocumentUploadInput,
	RequestLabResultUploadInput,
	RequestPhotoUploadInput,
	ResultsInboxApi,
	ResultsInboxCounts,
	RevenueSummary,
	ReviewInfo,
	ReviewsSummary,
	RxSuggestInput,
	RxSuggestResult,
	SalaryPaymentApi,
	ScheduleOverrideApi,
	ScheduleSurgeryInput,
	ScribeSoapInput,
	ScribeSoapResult,
	SearchResult,
	SellPackageInput,
	SetLabOrderRecheckInput,
	SetOrderPaymentInput,
	SetSalaryInput,
	SetWeeklyScheduleInput,
	SignConsentInput,
	SignupResult,
	SkinAnalysisApi,
	SkinLesionApi,
	SkinLesionMeasurementApi,
	StaffSalaryApi,
	SubmitReviewInput,
	SubmitReviewResult,
	SubscriptionRequestApi,
	SurgeryApi,
	SynthesisJobApi,
	SynthesisJobStartResponse,
	TaskApi,
	TaskCounts,
	TatReportQuery,
	TatReportResponse,
	TimelineEvent,
	TimelineResult,
	ToothConditionApi,
	TransferAdmissionInput,
	TreatmentPackageApi,
	TreatmentPlanItemApi,
	TreatmentPlanMapApi,
	UpdateAppointmentInput,
	UpdateBedStatusInput,
	UpdateBodyDrawingInput,
	UpdateClinicInput,
	UpdateClinicSupplyInput,
	UpdateCultureOrganismInput,
	UpdateDentalRecallInput,
	UpdateDocumentInput,
	UpdateEdStatusInput,
	UpdateEdTriageInput,
	UpdateExpenseInput,
	UpdateHomeCollectionInput,
	UpdateInjectableLotInput,
	UpdateInstrumentInput,
	UpdateLabOrderTestsInput,
	UpdateLabPanelInput,
	UpdateLabResultInput,
	UpdateLabTestInput,
	UpdateLoyaltySettingsInput,
	UpdateMedicationInput,
	UpdateMemberInput,
	UpdatePackageInput,
	UpdatePatientInput,
	UpdatePatientProblemInput,
	UpdatePermissionsInput,
	UpdatePharmacyItemInput,
	UpdatePhotoFollowupInput,
	UpdatePhotoInput,
	UpdatePregnancyInput,
	UpdatePrescriptionInput,
	UpdateQcMaterialInput,
	UpdateReagentCatalogInput,
	UpdateReagentLotInput,
	UpdateRecallInput,
	UpdateRecurringExpenseInput,
	UpdateReferralDoctorInput,
	UpdateReferralPartnerInput,
	UpdateReflexRuleInput,
	UpdateReminderTemplatesInput,
	UpdateServiceInput,
	UpdateSkinLesionInput,
	UpdateSupplierInput,
	UpdateSurgeryStatusInput,
	UpdateTaskInput,
	UpdateToothConditionInput,
	UpdateTreatmentPlanItemInput,
	UpdateTreatmentPlanMapInput,
	UpdateVisitInput,
	UpdateVitalsInput,
	UpdateWaitlistInput,
	UpsertInstrumentMappingInput,
	UpsertQcTargetInput,
	VisitAddendumApi,
	VisitTemplateApi,
	VoidSaleInput,
	WaitlistEntryApi,
	WalkInAppointmentInput,
	WardApi,
} from "@klyra/shared";

export interface ClinicBrandingApi {
	logo_key: string | null;
	logo_url: string | null;
	primary_color: string | null;
	accent_color: string | null;
	cover_image_key: string | null;
	cover_image_url: string | null;
	bio_ar: string | null;
	bio_en: string | null;
}

export interface ClinicApi {
	id: string;
	slug: string;
	name: { ar: string; en?: string };
	business_type: string;
	timezone: string;
	default_language: "ar" | "en";
	rtl_default: boolean;
	subscription_tier: string;
	trial_ends_at: string | null;
	subscribed_until: string | null;
	is_active: boolean;
	settings: Record<string, unknown>;
	feature_flags: Record<string, boolean>;
	branding: ClinicBrandingApi;
	created_at: string;
	updated_at: string;
}

export interface SchedulableMember {
	id: string;
	role: string;
	title: string | null;
	specialty: string | null;
	license_number: string | null;
	user_full_name: string;
}

export const auth = {
	config: () =>
		apiFetch<{
			public_signup: boolean;
			email_verification: boolean;
			captcha_site_key: string | null;
		}>("/auth/config", {
			method: "GET",
			auth: false,
		}),

	branches: () => apiFetch<BranchSummary[]>("/auth/branches"),

	switchBranch: (clinicId: string) =>
		apiFetch<AuthResponse>("/auth/switch-branch", {
			method: "POST",
			body: { clinic_id: clinicId },
		}),

	clinicSignup: (input: ClinicSignupInput) =>
		apiFetch<SignupResult>("/auth/clinic-signup", {
			method: "POST",
			body: input,
			auth: false,
		}),

	// Consume an email-verification link's token → logs the owner in (G5).
	verifyEmail: (token: string) =>
		apiFetch<AuthResponse>("/auth/verify-email", {
			method: "POST",
			body: { token },
			auth: false,
		}),

	// Re-send the verification email. Always succeeds (anti-enumeration).
	resendVerification: (email: string) =>
		apiFetch<{ sent: boolean }>("/auth/resend-verification", {
			method: "POST",
			body: { email },
			auth: false,
		}),

	login: (input: LoginInput) =>
		apiFetch<AuthResponse>("/auth/login", {
			method: "POST",
			body: input,
			auth: false,
		}),

	me: () => apiFetch<AuthResponse>("/auth/me", { method: "GET" }),

	permissions: () =>
		apiFetch<CurrentUserPermissions>("/auth/permissions", { method: "GET" }),

	logout: (refreshToken?: string) =>
		apiFetch<{ message: string }>("/auth/logout", {
			method: "POST",
			body: refreshToken ? { refreshToken } : {},
		}),

	refresh: (refreshToken: string) =>
		apiFetch<AuthResponse["tokens"]>("/auth/refresh", {
			method: "POST",
			body: { refreshToken },
			auth: false,
		}),

	changePassword: (currentPassword: string, newPassword: string) =>
		apiFetch<{ message: string }>("/auth/change-password", {
			method: "POST",
			body: { current_password: currentPassword, new_password: newPassword },
		}),
};

function qs(params: Record<string, unknown>): string {
	const sp = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v === undefined || v === null || v === "") continue;
		sp.set(k, String(v));
	}
	const s = sp.toString();
	return s ? `?${s}` : "";
}

export const patients = {
	list: (query: Partial<PatientsListQuery> = {}) =>
		apiFetch<PaginatedResponse<Patient>>(`/patients${qs(query)}`),

	get: (id: string) => apiFetch<Patient>(`/patients/${id}`),

	create: (input: CreatePatientInput) =>
		apiFetch<Patient>("/patients", { method: "POST", body: input }),

	import: (rows: ImportPatientRow[]) =>
		apiFetch<ImportPatientsResult>("/patients/import", {
			method: "POST",
			body: { rows },
		}),

	update: (id: string, input: UpdatePatientInput) =>
		apiFetch<Patient>(`/patients/${id}`, { method: "PATCH", body: input }),

	remove: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/patients/${id}`, {
			method: "DELETE",
		}),

	timeline: (
		id: string,
		opts: { types?: string[]; from?: string; to?: string; limit?: number } = {},
	) => {
		const q = new URLSearchParams();
		if (opts.types?.length) q.set("types", opts.types.join(","));
		if (opts.from) q.set("from", opts.from);
		if (opts.to) q.set("to", opts.to);
		if (opts.limit) q.set("limit", String(opts.limit));
		return apiFetch<TimelineResult>(`/patients/${id}/timeline?${q}`);
	},
};

export const appointments = {
	list: (query: Partial<AppointmentsListQuery> = {}) =>
		apiFetch<PaginatedResponse<AppointmentWithPatient>>(
			`/appointments${qs(query)}`,
		),

	get: (id: string) => apiFetch<Appointment>(`/appointments/${id}`),

	create: (input: CreateAppointmentInput) =>
		apiFetch<Appointment>("/appointments", { method: "POST", body: input }),

	/** Atomic walk-in: create + check-in (+ start) in one server transaction. */
	walkIn: (input: WalkInAppointmentInput) =>
		apiFetch<Appointment>("/appointments/walk-in", {
			method: "POST",
			body: input,
		}),

	/** Recurring series — occurrences that clash come back as `skipped`. */
	createRecurring: (input: CreateRecurringInput) =>
		apiFetch<CreateRecurringResult>("/appointments/recurring", {
			method: "POST",
			body: input,
		}),

	// ===== Waitlist =====
	waitlist: () =>
		apiFetch<WaitlistEntryApi[]>("/appointments/waitlist/entries"),

	addToWaitlist: (input: CreateWaitlistInput) =>
		apiFetch<WaitlistEntryApi>("/appointments/waitlist/entries", {
			method: "POST",
			body: input,
		}),

	updateWaitlist: (id: string, input: UpdateWaitlistInput) =>
		apiFetch<WaitlistEntryApi>(`/appointments/waitlist/entries/${id}`, {
			method: "PATCH",
			body: input,
		}),

	markWaitlistOffered: (id: string) =>
		apiFetch<WaitlistEntryApi>(`/appointments/waitlist/entries/${id}/offered`, {
			method: "POST",
		}),

	update: (id: string, input: UpdateAppointmentInput) =>
		apiFetch<Appointment>(`/appointments/${id}`, {
			method: "PATCH",
			body: input,
		}),

	setPreVisitIntake: (id: string, intake: PreVisitIntake | null) =>
		apiFetch<Appointment>(`/appointments/${id}/intake`, {
			method: "PATCH",
			body: { intake },
		}),

	transition: (id: string, action: AppointmentStatusAction, reason?: string) =>
		apiFetch<Appointment>(`/appointments/${id}/transition`, {
			method: "POST",
			body: { action, ...(reason ? { reason } : {}) },
		}),

	stats: (from: string, to: string) =>
		apiFetch<{ status: AppointmentStatus; count: number }[]>(
			`/appointments/stats${qs({ from, to })}`,
		),

	noShowRisk: (from: string, to: string) =>
		apiFetch<AppointmentNoShowRisk[]>(
			`/appointments/no-show-risk${qs({ from, to })}`,
		),
};

export interface AssistantToolCall {
	name: string;
	input: Record<string, unknown>;
	output: { ok: boolean; data?: unknown; error?: string };
}

export interface AssistantAskResponse {
	response: string;
	tool_calls: AssistantToolCall[];
	stop_reason: string | null;
	usage: { input_tokens: number; output_tokens: number };
}

export const scheduling = {
	listMembers: () => apiFetch<SchedulableMember[]>("/scheduling/members"),

	availableSlots: (query: AvailableSlotsQuery) =>
		apiFetch<AvailableSlotsResponse>(
			`/scheduling/available-slots${qs(query as unknown as Record<string, unknown>)}`,
		),

	getSchedule: (memberId: string) =>
		apiFetch<DoctorScheduleApi[]>(`/scheduling/members/${memberId}/schedule`),

	setSchedule: (memberId: string, input: SetWeeklyScheduleInput) =>
		apiFetch<DoctorScheduleApi[]>(`/scheduling/members/${memberId}/schedule`, {
			method: "PUT",
			body: input,
		}),

	listOverrides: (query: Partial<ListScheduleOverridesQuery> = {}) => {
		const p = new URLSearchParams();
		if (query.member_id) p.set("member_id", query.member_id);
		if (query.from) p.set("from", query.from);
		if (query.to) p.set("to", query.to);
		return apiFetch<ScheduleOverrideApi[]>(`/scheduling/overrides?${p}`);
	},

	createOverride: (input: CreateScheduleOverrideInput) =>
		apiFetch<ScheduleOverrideApi>("/scheduling/overrides", {
			method: "POST",
			body: input,
		}),

	deleteOverride: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/scheduling/overrides/${id}`, {
			method: "DELETE",
		}),
};

export const settingsApi = {
	getClinic: () => apiFetch<ClinicApi>("/settings/clinic"),

	updateClinic: (input: UpdateClinicInput) =>
		apiFetch<ClinicApi>("/settings/clinic", { method: "PATCH", body: input }),

	listServices: () => apiFetch<ClinicServiceApi[]>("/settings/services"),

	createService: (input: CreateServiceInput) =>
		apiFetch<ClinicServiceApi>("/settings/services", {
			method: "POST",
			body: input,
		}),

	updateService: (id: string, input: UpdateServiceInput) =>
		apiFetch<ClinicServiceApi>(`/settings/services/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteService: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/settings/services/${id}`, {
			method: "DELETE",
		}),

	requestLogoUpload: (input: {
		content_type: string;
		content_length: number;
	}) =>
		apiFetch<{ upload_url: string; key: string }>(
			"/settings/branding/logo/upload-url",
			{ method: "POST", body: input },
		),

	getReminderTemplates: () =>
		apiFetch<ReminderTemplatesResponse>("/settings/reminder-templates"),

	updateReminderTemplates: (input: UpdateReminderTemplatesInput) =>
		apiFetch<ReminderTemplatesResponse>("/settings/reminder-templates", {
			method: "PUT",
			body: input,
		}),

	// Treatment packages catalog
	listPackages: () => apiFetch<TreatmentPackageApi[]>("/settings/packages"),

	createPackage: (input: CreatePackageInput) =>
		apiFetch<TreatmentPackageApi>("/settings/packages", {
			method: "POST",
			body: input,
		}),

	updatePackage: (id: string, input: UpdatePackageInput) =>
		apiFetch<TreatmentPackageApi>(`/settings/packages/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deletePackage: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/settings/packages/${id}`, {
			method: "DELETE",
		}),

	// Self-service subscription (manual transfer + admin approval)
	getBankDetails: () =>
		apiFetch<PlatformBankDetails>("/settings/subscription/bank-details"),

	listSubscriptionRequests: () =>
		apiFetch<SubscriptionRequestApi[]>("/settings/subscription/requests"),

	createSubscriptionRequest: (input: CreateSubscriptionRequestInput) =>
		apiFetch<SubscriptionRequestApi>("/settings/subscription/requests", {
			method: "POST",
			body: input,
		}),

	// Data-portability export (owner-only): patient roster + billing as JSON.
	exportClinicData: () =>
		apiFetch<{
			exported_at: string;
			patient_count: number;
			invoice_count: number;
			truncated: boolean;
			[k: string]: unknown;
		}>("/settings/clinic/export"),
};

export const clinical = {
	// Visits
	createVisit: (input: CreateVisitInput) =>
		apiFetch<PatientVisitApi>("/clinical/visits", {
			method: "POST",
			body: input,
		}),

	getVisit: (id: string) => apiFetch<PatientVisitApi>(`/clinical/visits/${id}`),

	updateVisit: (id: string, input: UpdateVisitInput) =>
		apiFetch<PatientVisitApi>(`/clinical/visits/${id}`, {
			method: "PATCH",
			body: input,
		}),

	listVisitsForPatient: (patientId: string) =>
		apiFetch<PatientVisitApi[]>(`/clinical/patients/${patientId}/visits`),

	getVisitForAppointment: (appointmentId: string) =>
		apiFetch<PatientVisitApi | null>(
			`/clinical/appointments/${appointmentId}/visit`,
		),

	// Sign & lock + addendums
	signVisit: (id: string, acknowledgePendingResults = false) =>
		apiFetch<PatientVisitApi>(`/clinical/visits/${id}/sign`, {
			method: "POST",
			body: { acknowledge_pending_results: acknowledgePendingResults },
		}),

	listVisitAddendums: (visitId: string) =>
		apiFetch<VisitAddendumApi[]>(`/clinical/visits/${visitId}/addendums`),

	createVisitAddendum: (visitId: string, input: CreateVisitAddendumInput) =>
		apiFetch<VisitAddendumApi>(`/clinical/visits/${visitId}/addendums`, {
			method: "POST",
			body: input,
		}),

	// Visit templates (reusable pre-filled encounters)
	listVisitTemplates: () =>
		apiFetch<VisitTemplateApi[]>("/clinical/visit-templates"),
	createVisitTemplate: (input: CreateVisitTemplateInput) =>
		apiFetch<VisitTemplateApi>("/clinical/visit-templates", {
			method: "POST",
			body: input,
		}),
	deleteVisitTemplate: (id: string) =>
		apiFetch<{ id: string }>(`/clinical/visit-templates/${id}`, {
			method: "DELETE",
		}),

	// Prescriptions
	createPrescription: (input: CreatePrescriptionInput) =>
		apiFetch<PrescriptionApi>("/clinical/prescriptions", {
			method: "POST",
			body: input,
		}),

	updatePrescription: (id: string, input: UpdatePrescriptionInput) =>
		apiFetch<PrescriptionApi>(`/clinical/prescriptions/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deletePrescription: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/prescriptions/${id}`,
			{
				method: "DELETE",
			},
		),

	listPrescriptionsForPatient: (patientId: string) =>
		apiFetch<PrescriptionApi[]>(
			`/clinical/patients/${patientId}/prescriptions`,
		),

	listPrescriptionsForVisit: (visitId: string) =>
		apiFetch<PrescriptionApi[]>(`/clinical/visits/${visitId}/prescriptions`),

	// Medications library
	searchMedications: (q: string, limit = 8) =>
		apiFetch<ClinicMedicationApi[]>(
			`/clinical/medications/search?q=${encodeURIComponent(q)}&limit=${limit}`,
		),

	listMedications: () =>
		apiFetch<ClinicMedicationApi[]>("/clinical/medications"),

	createMedication: (input: CreateMedicationInput) =>
		apiFetch<ClinicMedicationApi>("/clinical/medications", {
			method: "POST",
			body: input,
		}),

	updateMedication: (id: string, input: UpdateMedicationInput) =>
		apiFetch<ClinicMedicationApi>(`/clinical/medications/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteMedication: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/medications/${id}`, {
			method: "DELETE",
		}),

	// Vitals
	createVitals: (input: CreateVitalsInput) =>
		apiFetch<PatientVitalsApi>("/clinical/vitals", {
			method: "POST",
			body: input,
		}),

	updateVitals: (id: string, input: UpdateVitalsInput) =>
		apiFetch<PatientVitalsApi>(`/clinical/vitals/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteVitals: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/vitals/${id}`, {
			method: "DELETE",
		}),

	listVitalsForVisit: (visitId: string) =>
		apiFetch<PatientVitalsApi[]>(`/clinical/visits/${visitId}/vitals`),

	listVitalsForPatient: (patientId: string) =>
		apiFetch<PatientVitalsApi[]>(`/clinical/patients/${patientId}/vitals`),

	// Onboarding (first-run setup checklist)
	onboardingStatus: () =>
		apiFetch<OnboardingStatusApi>("/clinical/onboarding-status"),

	// Cross-facility lab flow
	linkedLabs: () => apiFetch<LinkedLabApi[]>("/clinical/linked-labs"),
	incomingOrders: () =>
		apiFetch<IncomingLabOrderApi[]>("/clinical/incoming-orders"),
	recordIncomingResults: (input: RecordIncomingResultsInput) =>
		apiFetch<LabResultValueApi[]>("/clinical/incoming-orders/results", {
			method: "POST",
			body: input,
		}),
	completeIncomingOrder: (id: string) =>
		apiFetch<LabOrderApi>(`/clinical/incoming-orders/${id}/complete`, {
			method: "POST",
		}),
	acceptReferral: (id: string, input: AcceptReferralInput = {}) =>
		apiFetch<LabOrderApi>(`/clinical/incoming-orders/${id}/accept`, {
			method: "POST",
			body: input,
		}),
	rejectIncomingOrder: (id: string, input: RejectLabOrderInput) =>
		apiFetch<LabOrderApi>(`/clinical/incoming-orders/${id}/reject`, {
			method: "POST",
			body: input,
		}),

	// Home collection (lab phlebotomy dispatch)
	homeCollections: (status?: LabCollectionStatus) =>
		apiFetch<HomeCollectionApi[]>(
			`/clinical/lab-orders/home-collections${status ? `?status=${status}` : ""}`,
		),
	updateHomeCollection: (id: string, input: UpdateHomeCollectionInput) =>
		apiFetch<HomeCollectionApi>(`/clinical/lab-orders/${id}/collection`, {
			method: "PATCH",
			body: input,
		}),

	// Clinic-side outstanding referrals (routed to partners + status)
	outstandingReferrals: () =>
		apiFetch<OutstandingReferralRow[]>("/clinical/outstanding-referrals"),

	// Prescription templates (reusable medication sets)
	listPrescriptionTemplates: () =>
		apiFetch<PrescriptionTemplateApi[]>("/clinical/prescription-templates"),
	createPrescriptionTemplate: (input: CreatePrescriptionTemplateInput) =>
		apiFetch<PrescriptionTemplateApi>("/clinical/prescription-templates", {
			method: "POST",
			body: input,
		}),
	deletePrescriptionTemplate: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/prescription-templates/${id}`,
			{ method: "DELETE" },
		),

	// Facility daily revenue (lab + radiology) + payment capture
	revenue: (from: string, to: string) =>
		apiFetch<FacilityRevenueApi>(
			`/clinical/revenue?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
		),
	setOrderPayment: (id: string, input: SetOrderPaymentInput) =>
		apiFetch<{ id: string; payment_method: string | null }>(
			`/clinical/orders/${id}/payment`,
			{
				method: "PATCH",
				body: input,
			},
		),

	// Cross-facility pharmacy flow
	linkedPharmacies: () =>
		apiFetch<LinkedLabApi[]>("/clinical/linked-pharmacies"),
	incomingPrescriptions: () =>
		apiFetch<IncomingPrescriptionApi[]>("/clinical/incoming-prescriptions"),
	dispensePrescription: (
		id: string,
		pickup?: { pickup_code?: string; pickup_override?: boolean },
	) =>
		apiFetch<{ id: string; dispensed: boolean }>(
			`/clinical/incoming-prescriptions/${id}/dispense`,
			{ method: "POST", body: pickup ?? {} },
		),
	acceptPrescription: (
		id: string,
		pickup?: { pickup_code?: string; pickup_override?: boolean },
	) =>
		apiFetch<AcceptedPrescriptionApi>(
			`/clinical/incoming-prescriptions/${id}/accept`,
			{ method: "POST", body: pickup ?? {} },
		),

	// Lab orders
	createLabOrder: (input: CreateLabOrderInput) =>
		apiFetch<LabOrderApi>("/clinical/lab-orders", {
			method: "POST",
			body: input,
		}),

	getLabOrder: (id: string) =>
		apiFetch<LabOrderApi>(`/clinical/lab-orders/${id}`),

	updateLabOrderTests: (id: string, input: UpdateLabOrderTestsInput) =>
		apiFetch<LabOrderApi>(`/clinical/lab-orders/${id}/tests`, {
			method: "PATCH",
			body: input,
		}),

	deleteLabOrder: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/lab-orders/${id}`, {
			method: "DELETE",
		}),

	listLabOrdersForVisit: (visitId: string) =>
		apiFetch<LabOrderApi[]>(`/clinical/visits/${visitId}/lab-orders`),

	listLabOrdersForPatient: (patientId: string) =>
		apiFetch<LabOrderApi[]>(`/clinical/patients/${patientId}/lab-orders`),

	// Lab worklist (standalone-lab facing)
	labWorklist: (params: { status?: LabOrderStatus; limit?: number } = {}) => {
		const q = new URLSearchParams();
		if (params.status) q.set("status", params.status);
		if (params.limit) q.set("limit", String(params.limit));
		const qs = q.toString();
		return apiFetch<LabWorklistItem[]>(
			`/clinical/lab-orders/worklist${qs ? `?${qs}` : ""}`,
		);
	},

	completeLabOrder: (id: string) =>
		apiFetch<LabOrderApi>(`/clinical/lab-orders/${id}/complete`, {
			method: "POST",
		}),

	// Pre-completion integrity check: lists the obstacles (missing results /
	// unverified values / critical alerts pending) so the UI can show a
	// fix-it dialog instead of a bare 409 from the complete endpoint.
	labOrderCompletionReadiness: (id: string) =>
		apiFetch<LabCompletionReadiness>(
			`/clinical/lab-orders/${id}/completion-readiness`,
		),

	// Plant the default delta + reflex safety-net rules (idempotent).
	seedLabSafetyDefaults: () =>
		apiFetch<LabSafetySeedResult>("/clinical/lab-safety/seed-defaults", {
			method: "POST",
		}),

	cancelLabOrder: (id: string) =>
		apiFetch<LabOrderApi>(`/clinical/lab-orders/${id}/cancel`, {
			method: "POST",
		}),

	rejectLabOrder: (id: string, input: RejectLabOrderInput) =>
		apiFetch<LabOrderApi>(`/clinical/lab-orders/${id}/reject`, {
			method: "POST",
			body: input,
		}),

	setLabOrderRecheck: (id: string, input: SetLabOrderRecheckInput) =>
		apiFetch<LabOrderApi>(`/clinical/lab-orders/${id}/recheck`, {
			method: "POST",
			body: input,
		}),

	// Specimen lifecycle stamps (ISO 15189 §5.4/§5.5). Second stamp → 409.
	collectSpecimen: (id: string) =>
		apiFetch<LabOrderApi>(`/clinical/lab-orders/${id}/specimen/collect`, {
			method: "POST",
		}),
	receiveSpecimen: (id: string) =>
		apiFetch<LabOrderApi>(`/clinical/lab-orders/${id}/specimen/receive`, {
			method: "POST",
		}),

	getTatReport: (query: TatReportQuery = {}) => {
		const qs = new URLSearchParams();
		if (query.from) qs.set("from", query.from);
		if (query.to) qs.set("to", query.to);
		const suffix = qs.toString() ? `?${qs.toString()}` : "";
		return apiFetch<TatReportResponse>(`/clinical/lab-reports/tat${suffix}`);
	},

	// ===== Reflex rules =====
	listReflexRules: () =>
		apiFetch<ClinicReflexRuleApi[]>("/clinical/reflex-rules"),
	createReflexRule: (input: CreateReflexRuleInput) =>
		apiFetch<ClinicReflexRuleApi>("/clinical/reflex-rules", {
			method: "POST",
			body: input,
		}),
	updateReflexRule: (id: string, input: UpdateReflexRuleInput) =>
		apiFetch<ClinicReflexRuleApi>(`/clinical/reflex-rules/${id}`, {
			method: "PATCH",
			body: input,
		}),
	deleteReflexRule: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/reflex-rules/${id}`, {
			method: "DELETE",
		}),

	// ===== Reagent inventory =====
	listReagentCatalog: () =>
		apiFetch<ReagentCatalogApi[]>("/clinical/inventory/catalog"),
	createReagentCatalog: (input: CreateReagentCatalogInput) =>
		apiFetch<ReagentCatalogApi>("/clinical/inventory/catalog", {
			method: "POST",
			body: input,
		}),
	updateReagentCatalog: (id: string, input: UpdateReagentCatalogInput) =>
		apiFetch<ReagentCatalogApi>(`/clinical/inventory/catalog/${id}`, {
			method: "PATCH",
			body: input,
		}),
	deleteReagentCatalog: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/inventory/catalog/${id}`,
			{
				method: "DELETE",
			},
		),
	listReagentLots: (catalogId?: string) => {
		const suffix = catalogId
			? `?catalog_id=${encodeURIComponent(catalogId)}`
			: "";
		return apiFetch<ReagentLotApi[]>(`/clinical/inventory/lots${suffix}`);
	},
	createReagentLot: (input: CreateReagentLotInput) =>
		apiFetch<ReagentLotApi>("/clinical/inventory/lots", {
			method: "POST",
			body: input,
		}),
	updateReagentLot: (id: string, input: UpdateReagentLotInput) =>
		apiFetch<ReagentLotApi>(`/clinical/inventory/lots/${id}`, {
			method: "PATCH",
			body: input,
		}),
	consumeReagent: (input: RecordReagentConsumptionInput) =>
		apiFetch<ReagentConsumptionApi>("/clinical/inventory/consumptions", {
			method: "POST",
			body: input,
		}),
	listReagentConsumptions: () =>
		apiFetch<ReagentConsumptionApi[]>("/clinical/inventory/consumptions"),
	listLowStock: () =>
		apiFetch<ReagentLowStockRow[]>("/clinical/inventory/low-stock"),
	listExpiringSoon: () =>
		apiFetch<ReagentExpiringRow[]>("/clinical/inventory/expiring"),
	getReorderSuggestions: () =>
		apiFetch<ReorderSuggestionsResponse>(
			"/clinical/inventory/reorder-suggestions",
		),

	// ===== Microbiology MVP =====
	getCultureBundle: (orderId: string) =>
		apiFetch<CultureBundleApi>(
			`/clinical/microbiology/orders/${orderId}/bundle`,
		),
	createCultureOrganism: (input: CreateCultureOrganismInput) =>
		apiFetch<CultureOrganismApi>("/clinical/microbiology/organisms", {
			method: "POST",
			body: input,
		}),
	updateCultureOrganism: (id: string, input: UpdateCultureOrganismInput) =>
		apiFetch<CultureOrganismApi>(`/clinical/microbiology/organisms/${id}`, {
			method: "PATCH",
			body: input,
		}),
	deleteCultureOrganism: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/microbiology/organisms/${id}`,
			{ method: "DELETE" },
		),
	recordAst: (input: RecordAstInput) =>
		apiFetch<OrganismSensitivityApi[]>("/clinical/microbiology/ast", {
			method: "POST",
			body: input,
		}),

	// ===== Instruments (LIS Bridge) =====
	listInstruments: (withMappings = true) =>
		apiFetch<InstrumentApi[]>(
			`/clinical/instruments${withMappings ? "?withMappings=true" : ""}`,
		),
	createInstrument: (input: CreateInstrumentInput) =>
		apiFetch<InstrumentApi>("/clinical/instruments", {
			method: "POST",
			body: input,
		}),
	updateInstrument: (id: string, input: UpdateInstrumentInput) =>
		apiFetch<InstrumentApi>(`/clinical/instruments/${id}`, {
			method: "PATCH",
			body: input,
		}),
	rotateBridgeToken: (id: string) =>
		apiFetch<InstrumentApi>(`/clinical/instruments/${id}/rotate-token`, {
			method: "POST",
		}),
	deleteInstrument: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/instruments/${id}`, {
			method: "DELETE",
		}),
	upsertInstrumentMapping: (input: UpsertInstrumentMappingInput) =>
		apiFetch<InstrumentTestMappingApi>("/clinical/instruments/mappings", {
			method: "PUT",
			body: input,
		}),
	applyDeviceProfile: (instrumentId: string, profile: DeviceProfileKey) =>
		apiFetch<{ added: number }>(
			`/clinical/instruments/${instrumentId}/apply-profile`,
			{
				method: "POST",
				body: { profile },
			},
		),
	deleteInstrumentMapping: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/instruments/mappings/${id}`,
			{ method: "DELETE" },
		),

	// Lab tests library (autocomplete + management)
	searchLabTests: (q: string, limit = 8) =>
		apiFetch<ClinicLabTestApi[]>(
			`/clinical/lab-tests/search?q=${encodeURIComponent(q)}&limit=${limit}`,
		),

	listLabTests: () => apiFetch<ClinicLabTestApi[]>("/clinical/lab-tests"),

	createLabTest: (input: CreateLabTestInput) =>
		apiFetch<ClinicLabTestApi>("/clinical/lab-tests", {
			method: "POST",
			body: input,
		}),

	bulkImportLabTests: (input: BulkImportLabTestsInput) =>
		apiFetch<{ added: number; skipped: number }>(
			"/clinical/lab-tests/bulk-import",
			{
				method: "POST",
				body: input,
			},
		),

	updateLabTest: (id: string, input: UpdateLabTestInput) =>
		apiFetch<ClinicLabTestApi>(`/clinical/lab-tests/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteLabTest: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/lab-tests/${id}`, {
			method: "DELETE",
		}),

	// Lab panels / packages
	listLabPanels: () => apiFetch<ClinicLabPanelApi[]>("/clinical/lab-panels"),

	createLabPanel: (input: CreateLabPanelInput) =>
		apiFetch<ClinicLabPanelApi>("/clinical/lab-panels", {
			method: "POST",
			body: input,
		}),

	updateLabPanel: (id: string, input: UpdateLabPanelInput) =>
		apiFetch<ClinicLabPanelApi>(`/clinical/lab-panels/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteLabPanel: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/lab-panels/${id}`, {
			method: "DELETE",
		}),

	// Referral doctors + dues
	listReferralDoctors: () =>
		apiFetch<ReferralDoctorApi[]>("/clinical/referral-doctors"),

	createReferralDoctor: (input: CreateReferralDoctorInput) =>
		apiFetch<ReferralDoctorApi>("/clinical/referral-doctors", {
			method: "POST",
			body: input,
		}),

	updateReferralDoctor: (id: string, input: UpdateReferralDoctorInput) =>
		apiFetch<ReferralDoctorApi>(`/clinical/referral-doctors/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteReferralDoctor: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/referral-doctors/${id}`,
			{ method: "DELETE" },
		),

	referralDues: (from: string, to: string) =>
		apiFetch<ReferralDuesRow[]>(
			`/clinical/referral-doctors/dues?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
		),

	// Referral-partner commission (facility-facing: lab/radiology/pharmacy)
	listReferralPartners: () =>
		apiFetch<ReferralPartnerApi[]>("/clinical/referral-partners"),

	updateReferralPartner: (linkId: string, input: UpdateReferralPartnerInput) =>
		apiFetch<ReferralPartnerApi>(`/clinical/referral-partners/${linkId}`, {
			method: "PATCH",
			body: input,
		}),

	referralPartnerDues: (from: string, to: string) =>
		apiFetch<ReferralPartnerDuesRow[]>(
			`/clinical/referral-partners/dues?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
		),

	referralPartnerDuesDetail: (clinicId: string, from: string, to: string) =>
		apiFetch<ReferralPartnerDuesDetailRow[]>(
			`/clinical/referral-partners/${clinicId}/dues-detail?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
		),

	referralDoctorDuesDetail: (id: string, from: string, to: string) =>
		apiFetch<ReferralDuesDetailRow[]>(
			`/clinical/referral-doctors/${id}/dues-detail?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
		),

	// Lab results (file uploads attached to a lab order)
	requestLabResultUpload: (
		labOrderId: string,
		input: RequestLabResultUploadInput,
	) =>
		apiFetch<PresignedUploadResponse>(
			`/clinical/lab-orders/${labOrderId}/results/upload-url`,
			{
				method: "POST",
				body: input,
			},
		),

	confirmLabResultUpload: (resultId: string) =>
		apiFetch<LabResultApi>(`/clinical/lab-results/${resultId}/confirm`, {
			method: "POST",
		}),

	updateLabResult: (resultId: string, input: UpdateLabResultInput) =>
		apiFetch<LabResultApi>(`/clinical/lab-results/${resultId}`, {
			method: "PATCH",
			body: input,
		}),

	deleteLabResult: (resultId: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/lab-results/${resultId}`,
			{
				method: "DELETE",
			},
		),

	listLabResultsForOrder: (labOrderId: string) =>
		apiFetch<LabResultApi[]>(`/clinical/lab-orders/${labOrderId}/results`),

	listLabResultsForPatient: (patientId: string) =>
		apiFetch<LabResultApi[]>(`/clinical/patients/${patientId}/lab-results`),

	// Drug interaction check (AI-powered)
	checkInteractions: (input: CheckInteractionInput) =>
		apiFetch<CheckInteractionResponse>("/clinical/interactions/check", {
			method: "POST",
			body: input,
		}),

	// AI clinical scribe — dictation/narrative → structured SOAP draft
	generateSoapNote: (input: ScribeSoapInput) =>
		apiFetch<
			ScribeSoapResult & { ai_generated: boolean; disclaimer_ar: string }
		>("/clinical/scribe/soap", { method: "POST", body: input }),

	// AI clinical co-pilot — visit data → differential + workup + red-flags
	copilotAssess: (input: CopilotAssessInput) =>
		apiFetch<
			CopilotAssessResult & { ai_generated: boolean; disclaimer_ar: string }
		>("/clinical/copilot/assess", { method: "POST", body: input }),

	// AI encounter synthesis — whole visit + labs + radiology → one draft.
	// Async job: POST returns { job_id } immediately (the LLM work runs
	// server-side, detached); poll getSynthesisJob until succeeded/failed.
	synthesizeEncounter: (input: EncounterSynthesisInput) =>
		apiFetch<SynthesisJobStartResponse>("/clinical/encounter/synthesize", {
			method: "POST",
			body: input,
		}),

	getSynthesisJob: (jobId: string) =>
		apiFetch<SynthesisJobApi>(`/clinical/encounter/synthesize/jobs/${jobId}`),

	// Pre-synthesis: how many ordered tests are still pending for this patient.
	getPendingResultsCount: (patientId: string) =>
		apiFetch<PendingResultsCount>(
			`/clinical/encounter/pending-results/${patientId}`,
		),

	// AI after-visit summary — saved visit → patient-facing summary
	generateVisitSummary: (input: AvsInput) =>
		apiFetch<AvsResult & { ai_generated: boolean; disclaimer_ar: string }>(
			"/clinical/visit-summary",
			{ method: "POST", body: input },
		),

	// AI computer-assisted coding — diagnosis text → grounded ICD-10 suggestions
	suggestIcd: (input: IcdSuggestInput) =>
		apiFetch<IcdSuggestResult>("/clinical/icd-suggest", {
			method: "POST",
			body: input,
		}),

	// Full WHO ICD-10 2019 search (server-side over the 12k dataset)
	searchIcd10: (q: string, limit = 20) =>
		apiFetch<DiagnosisCode[]>(
			`/clinical/icd10/search?q=${encodeURIComponent(q)}&limit=${limit}`,
		),

	// Problem list (longitudinal, optionally ICD-10 coded)
	listProblemsForPatient: (patientId: string, status?: "active" | "resolved") =>
		apiFetch<PatientProblemApi[]>(
			`/clinical/patients/${patientId}/problems${status ? `?status=${status}` : ""}`,
		),
	createProblem: (input: CreatePatientProblemInput) =>
		apiFetch<PatientProblemApi>("/clinical/problems", {
			method: "POST",
			body: input,
		}),
	updateProblem: (id: string, input: UpdatePatientProblemInput) =>
		apiFetch<PatientProblemApi>(`/clinical/problems/${id}`, {
			method: "PATCH",
			body: input,
		}),
	resolveProblem: (id: string) =>
		apiFetch<PatientProblemApi>(`/clinical/problems/${id}/resolve`, {
			method: "POST",
		}),
	deleteProblem: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/problems/${id}`, {
			method: "DELETE",
		}),

	// AI prescription draft — visit plan → structured Rx items the doctor reviews
	suggestPrescription: (input: RxSuggestInput) =>
		apiFetch<RxSuggestResult>("/clinical/prescription-suggest", {
			method: "POST",
			body: input,
		}),

	// Structured lab values
	createLabResultValues: (input: CreateLabResultValuesBatchInput) =>
		apiFetch<LabResultValueApi[]>("/clinical/lab-values", {
			method: "POST",
			body: input,
		}),

	// AI extraction: photo/PDF of an outside report → draft values to review.
	extractLabValues: (input: ExtractLabValuesInput) =>
		apiFetch<ExtractLabValuesResult>("/clinical/lab-values/extract", {
			method: "POST",
			body: input,
		}),

	// AI lab review: doctor-facing trend reading + considerations over history.
	reviewLabValues: (patientId: string) =>
		apiFetch<LabReviewResult>("/clinical/lab-review", {
			method: "POST",
			body: { patient_id: patientId },
		}),

	// Panel expansion: component grid for an ordered test (CBC → 13 analytes).
	listLabComponents: (testName: string) =>
		apiFetch<LabComponentApi[]>(
			`/clinical/lab-components?test_name=${encodeURIComponent(testName)}`,
		),

	listLabValuesForResult: (resultId: string) =>
		apiFetch<LabResultValueApi[]>(`/clinical/lab-results/${resultId}/values`),

	listLabValuesForOrder: (orderId: string) =>
		apiFetch<LabResultValueApi[]>(`/clinical/lab-orders/${orderId}/values`),

	listLabValuesForPatient: (patientId: string) =>
		apiFetch<LabResultValueApi[]>(`/clinical/patients/${patientId}/lab-values`),

	deleteLabResultValue: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/lab-values/${id}`, {
			method: "DELETE",
		}),

	verifyLabValues: (valueIds: string[]) =>
		apiFetch<LabResultValueApi[]>("/clinical/lab-values/verify", {
			method: "POST",
			body: { value_ids: valueIds },
		}),

	// Amend a verified value (ISO 15189 §5.8.3) — reason mandatory.
	amendLabValue: (id: string, input: AmendLabValueInput) =>
		apiFetch<LabResultValueApi>(`/clinical/lab-values/${id}/amend`, {
			method: "POST",
			body: input,
		}),

	// Amendment history behind the «معدّل» chip (fetched on demand).
	listLabValueAmendments: (id: string) =>
		apiFetch<LabValueAmendmentApi[]>(`/clinical/lab-values/${id}/amendments`),

	// Bulk sign-off of every unverified value of one order (results inbox).
	verifyLabValuesForOrder: (orderId: string) =>
		apiFetch<LabResultValueApi[]>(
			`/clinical/lab-orders/${orderId}/verify-values`,
			{ method: "POST" },
		),

	// ===== Results inbox (doctor's unified review queue) =====
	resultsInbox: () => apiFetch<ResultsInboxApi>("/clinical/results-inbox"),
	resultsInboxCounts: () =>
		apiFetch<ResultsInboxCounts>("/clinical/results-inbox/count"),

	// ===== Quality Control (ISO 15189 §5.6) =====
	listQcMaterials: (withTargets = false) =>
		apiFetch<QcMaterialApi[]>(
			`/clinical/qc/materials${withTargets ? "?withTargets=true" : ""}`,
		),

	createQcMaterial: (input: CreateQcMaterialInput) =>
		apiFetch<QcMaterialApi>("/clinical/qc/materials", {
			method: "POST",
			body: input,
		}),

	updateQcMaterial: (id: string, input: UpdateQcMaterialInput) =>
		apiFetch<QcMaterialApi>(`/clinical/qc/materials/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteQcMaterial: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/qc/materials/${id}`, {
			method: "DELETE",
		}),

	upsertQcTarget: (input: UpsertQcTargetInput) =>
		apiFetch<QcTargetApi>("/clinical/qc/targets", {
			method: "PUT",
			body: input,
		}),

	deleteQcTarget: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/qc/targets/${id}`, {
			method: "DELETE",
		}),

	recordQcRuns: (input: RecordQcRunsInput) =>
		apiFetch<QcRunApi[]>("/clinical/qc/runs", { method: "POST", body: input }),

	getQcChart: (query: QcChartQuery) => {
		const qs = new URLSearchParams({
			material_id: query.material_id,
			test_name: query.test_name,
			limit: String(query.limit ?? 60),
		});
		return apiFetch<QcChartResponse>(`/clinical/qc/chart?${qs.toString()}`);
	},

	// Delta check (pre-save) — used by the result-entry UI to surface a
	// warning modal before committing values that swing implausibly from
	// the patient's most recent prior measurement.
	checkLabValueDelta: (input: DeltaCheckInput) =>
		apiFetch<DeltaCheckResponse>("/clinical/lab-values/delta-check", {
			method: "POST",
			body: input,
		}),

	// ===== Critical-value alerts (ISO 15189 §5.8.7) =====
	listPendingCriticalAlerts: () =>
		apiFetch<CriticalAlertApi[]>("/clinical/critical-alerts/pending"),

	listCriticalAlerts: (query: ListCriticalAlertsQuery = { limit: 50 }) => {
		const qs = new URLSearchParams();
		if (query.status) qs.set("status", query.status);
		if (query.from) qs.set("from", query.from);
		if (query.to) qs.set("to", query.to);
		if (query.limit) qs.set("limit", String(query.limit));
		return apiFetch<CriticalAlertApi[]>(
			`/clinical/critical-alerts?${qs.toString()}`,
		);
	},

	getCriticalAlert: (id: string) =>
		apiFetch<CriticalAlertApi>(`/clinical/critical-alerts/${id}`),

	markCriticalAlertNotified: (
		id: string,
		input: MarkCriticalAlertNotifiedInput,
	) =>
		apiFetch<CriticalAlertApi>(`/clinical/critical-alerts/${id}/notify`, {
			method: "PATCH",
			body: input,
		}),

	batchMarkCriticalAlertsNotified: (
		input: BatchMarkCriticalAlertsNotifiedInput,
	) =>
		apiFetch<CriticalAlertApi[]>("/clinical/critical-alerts/notify-batch", {
			method: "PATCH",
			body: input,
		}),

	markCriticalAlertAcknowledged: (id: string) =>
		apiFetch<CriticalAlertApi>(`/clinical/critical-alerts/${id}/acknowledge`, {
			method: "PATCH",
		}),

	// Dental: odontogram + treatment plan
	getDentalChart: (patientId: string) =>
		apiFetch<DentalChartApi>(`/clinical/dental/chart/${patientId}`),

	createToothCondition: (input: CreateToothConditionInput) =>
		apiFetch<ToothConditionApi>("/clinical/dental/conditions", {
			method: "POST",
			body: input,
		}),

	updateToothCondition: (id: string, input: UpdateToothConditionInput) =>
		apiFetch<ToothConditionApi>(`/clinical/dental/conditions/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteToothCondition: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/dental/conditions/${id}`,
			{ method: "DELETE" },
		),

	createTreatmentItem: (input: CreateTreatmentPlanItemInput) =>
		apiFetch<TreatmentPlanItemApi>("/clinical/dental/treatment-items", {
			method: "POST",
			body: input,
		}),

	updateTreatmentItem: (id: string, input: UpdateTreatmentPlanItemInput) =>
		apiFetch<TreatmentPlanItemApi>(`/clinical/dental/treatment-items/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteTreatmentItem: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/dental/treatment-items/${id}`,
			{ method: "DELETE" },
		),

	convertTreatmentItemsToInvoice: (
		input: ConvertTreatmentItemsToInvoiceInput,
	) =>
		apiFetch<InvoiceApi>("/clinical/dental/treatment-items/invoice", {
			method: "POST",
			body: input,
		}),

	// Multi-branch consolidated report (chain rollup)
	branchesReport: () => apiFetch<BranchesReport>("/clinical/branches-report"),

	// Reviews / reputation
	reviewsSummary: () => apiFetch<ReviewsSummary>("/clinical/reviews"),
	setGoogleReviewUrl: (google_review_url: string | null) =>
		apiFetch<{ google_review_url: string | null }>(
			"/clinical/reviews/google-url",
			{
				method: "PATCH",
				body: { google_review_url },
			},
		),

	// Finance: expenses + P&L report
	financeReport: (q: FinanceReportQuery = {}) => {
		const qs = new URLSearchParams();
		if (q.from) qs.set("from", q.from);
		if (q.to) qs.set("to", q.to);
		const s = qs.toString();
		return apiFetch<FinanceReportResponse>(
			`/clinical/finance/report${s ? `?${s}` : ""}`,
		);
	},
	listExpenses: (q: FinanceReportQuery = {}) => {
		const qs = new URLSearchParams();
		if (q.from) qs.set("from", q.from);
		if (q.to) qs.set("to", q.to);
		const s = qs.toString();
		return apiFetch<ClinicExpenseApi[]>(
			`/clinical/finance/expenses${s ? `?${s}` : ""}`,
		);
	},
	exportExpensesCsv: (q: FinanceReportQuery = {}) => {
		const qs = new URLSearchParams();
		if (q.from) qs.set("from", q.from);
		if (q.to) qs.set("to", q.to);
		const s = qs.toString();
		return apiDownload(
			`/clinical/finance/expenses/export${s ? `?${s}` : ""}`,
			`expenses-${new Date().toISOString().slice(0, 10)}.csv`,
		);
	},
	createExpense: (input: CreateExpenseInput) =>
		apiFetch<ClinicExpenseApi>("/clinical/finance/expenses", {
			method: "POST",
			body: input,
		}),
	updateExpense: (id: string, input: UpdateExpenseInput) =>
		apiFetch<ClinicExpenseApi>(`/clinical/finance/expenses/${id}`, {
			method: "PATCH",
			body: input,
		}),
	deleteExpense: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/finance/expenses/${id}`,
			{ method: "DELETE" },
		),

	// Payroll: staff salaries + disbursements
	listSalaries: () =>
		apiFetch<StaffSalaryApi[]>("/clinical/finance/payroll/salaries"),
	setSalary: (input: SetSalaryInput) =>
		apiFetch<StaffSalaryApi>("/clinical/finance/payroll/salaries", {
			method: "PUT",
			body: input,
		}),
	listSalaryPayments: (q: ListSalaryPaymentsQuery = {}) => {
		const qs = new URLSearchParams();
		if (q.from) qs.set("from", q.from);
		if (q.to) qs.set("to", q.to);
		if (q.member_id) qs.set("member_id", q.member_id);
		const s = qs.toString();
		return apiFetch<SalaryPaymentApi[]>(
			`/clinical/finance/payroll/payments${s ? `?${s}` : ""}`,
		);
	},
	paySalary: (input: PaySalaryInput) =>
		apiFetch<SalaryPaymentApi>("/clinical/finance/payroll/payments", {
			method: "POST",
			body: input,
		}),

	// Recurring expenses (templates)
	listRecurringExpenses: () =>
		apiFetch<RecurringExpenseApi[]>("/clinical/finance/recurring"),
	createRecurringExpense: (input: CreateRecurringExpenseInput) =>
		apiFetch<RecurringExpenseApi>("/clinical/finance/recurring", {
			method: "POST",
			body: input,
		}),
	updateRecurringExpense: (id: string, input: UpdateRecurringExpenseInput) =>
		apiFetch<RecurringExpenseApi>(`/clinical/finance/recurring/${id}`, {
			method: "PATCH",
			body: input,
		}),
	deleteRecurringExpense: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/finance/recurring/${id}`,
			{ method: "DELETE" },
		),

	// Unified task inbox
	listTasks: (q: ListTasksQuery = {}) => {
		const qs = new URLSearchParams();
		if (q.scope) qs.set("scope", q.scope);
		if (q.status) qs.set("status", q.status);
		if (q.patient_id) qs.set("patient_id", q.patient_id);
		if (q.limit) qs.set("limit", String(q.limit));
		const s = qs.toString();
		return apiFetch<TaskApi[]>(`/clinical/tasks${s ? `?${s}` : ""}`);
	},
	taskCounts: () => apiFetch<TaskCounts>("/clinical/tasks/counts"),
	createTask: (input: CreateTaskInput) =>
		apiFetch<TaskApi>("/clinical/tasks", { method: "POST", body: input }),
	updateTask: (id: string, input: UpdateTaskInput) =>
		apiFetch<TaskApi>(`/clinical/tasks/${id}`, {
			method: "PATCH",
			body: input,
		}),
	claimTask: (id: string) =>
		apiFetch<TaskApi>(`/clinical/tasks/${id}/claim`, { method: "POST" }),
	deleteTask: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/tasks/${id}`, {
			method: "DELETE",
		}),

	// Dental per-tooth images (intra-oral photos / X-rays)
	listToothImages: (patientId: string) =>
		apiFetch<DentalToothImageApi[]>(
			`/clinical/dental/patients/${patientId}/tooth-images`,
		),

	presignToothImage: (patientId: string, input: DentalToothImagePresignInput) =>
		apiFetch<DentalToothImagePresignResponse>(
			`/clinical/dental/patients/${patientId}/tooth-images/presign`,
			{ method: "POST", body: input },
		),

	confirmToothImage: (patientId: string, input: ConfirmDentalToothImageInput) =>
		apiFetch<DentalToothImageApi>(
			`/clinical/dental/patients/${patientId}/tooth-images/confirm`,
			{ method: "POST", body: input },
		),

	deleteToothImage: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/dental/tooth-images/${id}`,
			{
				method: "DELETE",
			},
		),

	// Dental periodontal charting
	listPerioExams: (patientId: string) =>
		apiFetch<PeriodontalExamApi[]>(
			`/clinical/dental/patients/${patientId}/perio-exams`,
		),

	createPerioExam: (input: CreatePeriodontalExamInput) =>
		apiFetch<PeriodontalExamApi>("/clinical/dental/perio-exams", {
			method: "POST",
			body: input,
		}),

	deletePerioExam: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/dental/perio-exams/${id}`,
			{
				method: "DELETE",
			},
		),

	// Dental recalls (prevention reminders)
	listDueRecalls: () =>
		apiFetch<DentalRecallApi[]>("/clinical/dental/recalls/due"),

	listRecalls: (patientId: string) =>
		apiFetch<DentalRecallApi[]>(
			`/clinical/dental/patients/${patientId}/recalls`,
		),

	createRecall: (input: CreateDentalRecallInput) =>
		apiFetch<DentalRecallApi>("/clinical/dental/recalls", {
			method: "POST",
			body: input,
		}),

	updateRecall: (id: string, input: UpdateDentalRecallInput) =>
		apiFetch<DentalRecallApi>(`/clinical/dental/recalls/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteRecall: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/dental/recalls/${id}`,
			{
				method: "DELETE",
			},
		),

	// Treatment packages (sold instances)
	listPatientPackages: (patientId: string) =>
		apiFetch<PatientPackageApi[]>(`/clinical/patients/${patientId}/packages`),

	getPatientPackage: (id: string) =>
		apiFetch<PatientPackageApi>(`/clinical/packages/${id}`),

	sellPackage: (input: SellPackageInput) =>
		apiFetch<PatientPackageApi>("/clinical/packages/sell", {
			method: "POST",
			body: input,
		}),

	redeemSession: (id: string, input: RedeemSessionInput) =>
		apiFetch<PatientPackageApi>(`/clinical/packages/${id}/redeem`, {
			method: "POST",
			body: input,
		}),

	cancelPackage: (id: string) =>
		apiFetch<PatientPackageApi>(`/clinical/packages/${id}/cancel`, {
			method: "POST",
			body: {},
		}),

	// Dermatology skin map (lesions)
	getSkinMap: (patientId: string) =>
		apiFetch<SkinLesionApi[]>(`/clinical/patients/${patientId}/skin-map`),

	createSkinLesion: (input: CreateSkinLesionInput) =>
		apiFetch<SkinLesionApi>("/clinical/skin-lesions", {
			method: "POST",
			body: input,
		}),

	updateSkinLesion: (id: string, input: UpdateSkinLesionInput) =>
		apiFetch<SkinLesionApi>(`/clinical/skin-lesions/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteSkinLesion: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/skin-lesions/${id}`, {
			method: "DELETE",
		}),

	// Dermatology lesion monitoring timeline
	listSkinMeasurements: (lesionId: string) =>
		apiFetch<SkinLesionMeasurementApi[]>(
			`/clinical/skin-lesions/${lesionId}/measurements`,
		),

	addSkinMeasurement: (input: CreateSkinLesionMeasurementInput) =>
		apiFetch<SkinLesionMeasurementApi>("/clinical/skin-lesion-measurements", {
			method: "POST",
			body: input,
		}),

	deleteSkinMeasurement: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/skin-lesion-measurements/${id}`,
			{
				method: "DELETE",
			},
		),

	// Dermatology AI before/after photo comparison
	compareLesionPhotos: (input: CompareLesionInput) =>
		apiFetch<LesionComparisonApi>("/clinical/skin-lesions/compare", {
			method: "POST",
			body: input,
		}),

	listLesionComparisons: (lesionId: string) =>
		apiFetch<LesionComparisonApi[]>(
			`/clinical/skin-lesions/${lesionId}/comparisons`,
		),

	// Obstetrics — pregnancy records
	listPregnancies: (patientId: string) =>
		apiFetch<PregnancyApi[]>(`/clinical/patients/${patientId}/pregnancies`),

	createPregnancy: (input: CreatePregnancyInput) =>
		apiFetch<PregnancyApi>("/clinical/pregnancies", {
			method: "POST",
			body: input,
		}),

	updatePregnancy: (id: string, input: UpdatePregnancyInput) =>
		apiFetch<PregnancyApi>(`/clinical/pregnancies/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deletePregnancy: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/pregnancies/${id}`, {
			method: "DELETE",
		}),

	// Antenatal checkups (per-visit measurements + safety flags)
	listCheckups: (pregnancyId: string) =>
		apiFetch<AntenatalCheckupApi[]>(
			`/clinical/pregnancies/${pregnancyId}/checkups`,
		),

	addCheckup: (input: CreateAntenatalCheckupInput) =>
		apiFetch<AntenatalCheckupApi>("/clinical/antenatal-checkups", {
			method: "POST",
			body: input,
		}),

	deleteCheckup: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/antenatal-checkups/${id}`,
			{
				method: "DELETE",
			},
		),

	// Patient recalls (cross-specialty bring-backs)
	listClinicRecalls: (query: Partial<RecallsListQuery> = {}) => {
		const p = new URLSearchParams();
		if (query.status) p.set("status", query.status);
		if (query.due_before) p.set("due_before", query.due_before);
		const suffix = p.toString() ? `?${p.toString()}` : "";
		return apiFetch<PatientRecallApi[]>(`/clinical/recalls${suffix}`);
	},
	listPatientRecalls: (patientId: string) =>
		apiFetch<PatientRecallApi[]>(`/clinical/patients/${patientId}/recalls`),
	createPatientRecall: (input: CreateRecallInput) =>
		apiFetch<PatientRecallApi>("/clinical/recalls", {
			method: "POST",
			body: input,
		}),
	updatePatientRecall: (id: string, input: UpdateRecallInput) =>
		apiFetch<PatientRecallApi>(`/clinical/recalls/${id}`, {
			method: "PATCH",
			body: input,
		}),
	deletePatientRecall: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/recalls/${id}`, {
			method: "DELETE",
		}),

	// Patient intelligence (care-gaps + trends)
	patientIntelligence: (patientId: string) =>
		apiFetch<PatientIntelligenceResponse>(
			`/clinical/patients/${patientId}/intelligence`,
		),

	// Digital consent (templates + signed forms)
	listConsentTemplates: () =>
		apiFetch<ConsentTemplateApi[]>("/clinical/consent-templates"),
	createConsentTemplate: (input: CreateConsentTemplateInput) =>
		apiFetch<ConsentTemplateApi>("/clinical/consent-templates", {
			method: "POST",
			body: input,
		}),
	deleteConsentTemplate: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/consent-templates/${id}`,
			{ method: "DELETE" },
		),
	listConsentForms: (patientId: string) =>
		apiFetch<ConsentFormApi[]>(`/clinical/patients/${patientId}/consents`),
	createConsentForm: (input: CreateConsentFormInput) =>
		apiFetch<ConsentFormApi>("/clinical/consents", {
			method: "POST",
			body: input,
		}),
	signConsentForm: (id: string, input: SignConsentInput) =>
		apiFetch<ConsentFormApi>(`/clinical/consents/${id}/sign`, {
			method: "POST",
			body: input,
		}),
	deleteConsentForm: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/consents/${id}`, {
			method: "DELETE",
		}),

	// Ophthalmology — eye exams
	listEyeExams: (patientId: string) =>
		apiFetch<EyeExamApi[]>(`/clinical/patients/${patientId}/eye-exams`),
	createEyeExam: (input: CreateEyeExamInput) =>
		apiFetch<EyeExamApi>("/clinical/eye-exams", {
			method: "POST",
			body: input,
		}),
	deleteEyeExam: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/eye-exams/${id}`, {
			method: "DELETE",
		}),

	// Cardiology assessments
	listCardiacAssessments: (patientId: string) =>
		apiFetch<CardiacAssessmentApi[]>(
			`/clinical/patients/${patientId}/cardiac-assessments`,
		),
	createCardiacAssessment: (input: CreateCardiacAssessmentInput) =>
		apiFetch<CardiacAssessmentApi>("/clinical/cardiac-assessments", {
			method: "POST",
			body: input,
		}),
	deleteCardiacAssessment: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/cardiac-assessments/${id}`,
			{ method: "DELETE" },
		),

	// Internal medicine chronic-care reviews
	listInternalMedAssessments: (patientId: string) =>
		apiFetch<InternalMedAssessmentApi[]>(
			`/clinical/patients/${patientId}/internal-med-assessments`,
		),
	createInternalMedAssessment: (input: CreateInternalMedAssessmentInput) =>
		apiFetch<InternalMedAssessmentApi>("/clinical/internal-med-assessments", {
			method: "POST",
			body: input,
		}),
	deleteInternalMedAssessment: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/internal-med-assessments/${id}`,
			{ method: "DELETE" },
		),

	// Orthopedic assessments
	listOrthopedicAssessments: (patientId: string) =>
		apiFetch<OrthopedicAssessmentApi[]>(
			`/clinical/patients/${patientId}/orthopedic-assessments`,
		),
	createOrthopedicAssessment: (input: CreateOrthopedicAssessmentInput) =>
		apiFetch<OrthopedicAssessmentApi>("/clinical/orthopedic-assessments", {
			method: "POST",
			body: input,
		}),
	deleteOrthopedicAssessment: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/orthopedic-assessments/${id}`,
			{ method: "DELETE" },
		),

	// ENT assessments
	listEntAssessments: (patientId: string) =>
		apiFetch<EntAssessmentApi[]>(
			`/clinical/patients/${patientId}/ent-assessments`,
		),
	createEntAssessment: (input: CreateEntAssessmentInput) =>
		apiFetch<EntAssessmentApi>("/clinical/ent-assessments", {
			method: "POST",
			body: input,
		}),
	deleteEntAssessment: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/ent-assessments/${id}`,
			{ method: "DELETE" },
		),

	// Psychiatry assessments (PHQ-9 / GAD-7)
	listPsychAssessments: (patientId: string) =>
		apiFetch<PsychAssessmentApi[]>(
			`/clinical/patients/${patientId}/psych-assessments`,
		),
	createPsychAssessment: (input: CreatePsychAssessmentInput) =>
		apiFetch<PsychAssessmentApi>("/clinical/psych-assessments", {
			method: "POST",
			body: input,
		}),
	deletePsychAssessment: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/psych-assessments/${id}`,
			{ method: "DELETE" },
		),

	// Physiotherapy — sessions
	listPhysioSessions: (patientId: string) =>
		apiFetch<PhysioSessionApi[]>(
			`/clinical/patients/${patientId}/physio-sessions`,
		),
	createPhysioSession: (input: CreatePhysioSessionInput) =>
		apiFetch<PhysioSessionApi>("/clinical/physio-sessions", {
			method: "POST",
			body: input,
		}),
	deletePhysioSession: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/physio-sessions/${id}`,
			{ method: "DELETE" },
		),

	// Obstetric ultrasounds (structured biometry + dating reconciliation)
	listUltrasounds: (pregnancyId: string) =>
		apiFetch<ObstetricUltrasoundApi[]>(
			`/clinical/pregnancies/${pregnancyId}/ultrasounds`,
		),
	addUltrasound: (input: CreateObstetricUltrasoundInput) =>
		apiFetch<ObstetricUltrasoundApi>("/clinical/obstetric-ultrasounds", {
			method: "POST",
			body: input,
		}),
	deleteUltrasound: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/obstetric-ultrasounds/${id}`,
			{
				method: "DELETE",
			},
		),

	// Pediatrics — growth measurements
	listGrowth: (patientId: string) =>
		apiFetch<GrowthMeasurementApi[]>(`/clinical/patients/${patientId}/growth`),

	createGrowth: (input: CreateGrowthMeasurementInput) =>
		apiFetch<GrowthMeasurementApi>("/clinical/growth", {
			method: "POST",
			body: input,
		}),

	deleteGrowth: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/growth/${id}`, {
			method: "DELETE",
		}),

	// Pediatrics — childhood immunizations (EPI tracking)
	listImmunizations: (patientId: string) =>
		apiFetch<ChildImmunizationApi[]>(
			`/clinical/patients/${patientId}/immunizations`,
		),

	recordImmunization: (input: RecordImmunizationInput) =>
		apiFetch<ChildImmunizationApi>("/clinical/immunizations", {
			method: "POST",
			body: input,
		}),

	deleteImmunization: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/immunizations/${id}`,
			{
				method: "DELETE",
			},
		),

	// Pharmacy inventory
	listPharmacyItems: () =>
		apiFetch<PharmacyItemApi[]>("/clinical/pharmacy/items"),
	pharmacyLowStock: () =>
		apiFetch<PharmacyItemApi[]>("/clinical/pharmacy/low-stock"),
	pharmacyExpiring: () =>
		apiFetch<PharmacyItemApi[]>("/clinical/pharmacy/expiring"),
	createPharmacyItem: (input: CreatePharmacyItemInput) =>
		apiFetch<PharmacyItemApi>("/clinical/pharmacy/items", {
			method: "POST",
			body: input,
		}),
	updatePharmacyItem: (id: string, input: UpdatePharmacyItemInput) =>
		apiFetch<PharmacyItemApi>(`/clinical/pharmacy/items/${id}`, {
			method: "PATCH",
			body: input,
		}),
	deletePharmacyItem: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/pharmacy/items/${id}`,
			{ method: "DELETE" },
		),
	adjustPharmacyStock: (input: AdjustPharmacyStockInput) =>
		apiFetch<PharmacyItemApi>("/clinical/pharmacy/adjust-stock", {
			method: "POST",
			body: input,
		}),

	// Pharmacy POS (sales)
	createPharmacySale: (input: CreateSaleInput) =>
		apiFetch<PharmacySaleApi>("/clinical/pharmacy/sales", {
			method: "POST",
			body: input,
		}),
	lookupPrescription: (query: string) =>
		apiFetch<PrescriptionLookupResult[]>(
			"/clinical/pharmacy/lookup-prescription",
			{ method: "POST", body: { query } },
		),
	listPharmacySales: (
		params: { from?: string; to?: string; limit?: number } = {},
	) => {
		const qs = new URLSearchParams();
		if (params.from) qs.set("from", params.from);
		if (params.to) qs.set("to", params.to);
		if (params.limit) qs.set("limit", String(params.limit));
		const suffix = qs.toString() ? `?${qs.toString()}` : "";
		return apiFetch<PharmacySaleApi[]>(`/clinical/pharmacy/sales${suffix}`);
	},
	getPharmacySale: (id: string) =>
		apiFetch<PharmacySaleApi>(`/clinical/pharmacy/sales/${id}`),
	voidPharmacySale: (id: string, input: VoidSaleInput) =>
		apiFetch<PharmacySaleApi>(`/clinical/pharmacy/sales/${id}/void`, {
			method: "POST",
			body: input,
		}),
	pharmacySalesReport: (params: { from?: string; to?: string } = {}) => {
		const qs = new URLSearchParams();
		if (params.from) qs.set("from", params.from);
		if (params.to) qs.set("to", params.to);
		const suffix = qs.toString() ? `?${qs.toString()}` : "";
		return apiFetch<PharmacySalesReportApi>(
			`/clinical/pharmacy/sales/report${suffix}`,
		);
	},

	// Pharmacy procurement (suppliers + purchase orders)
	listPharmacySuppliers: () =>
		apiFetch<PharmacySupplierApi[]>("/clinical/pharmacy/suppliers"),
	createPharmacySupplier: (input: CreateSupplierInput) =>
		apiFetch<PharmacySupplierApi>("/clinical/pharmacy/suppliers", {
			method: "POST",
			body: input,
		}),
	updatePharmacySupplier: (id: string, input: UpdateSupplierInput) =>
		apiFetch<PharmacySupplierApi>(`/clinical/pharmacy/suppliers/${id}`, {
			method: "PATCH",
			body: input,
		}),
	deletePharmacySupplier: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/pharmacy/suppliers/${id}`,
			{ method: "DELETE" },
		),
	listPharmacyPurchaseOrders: () =>
		apiFetch<PharmacyPurchaseOrderApi[]>("/clinical/pharmacy/purchase-orders"),
	getPharmacyPurchaseOrder: (id: string) =>
		apiFetch<PharmacyPurchaseOrderApi>(
			`/clinical/pharmacy/purchase-orders/${id}`,
		),
	createPharmacyPurchaseOrder: (input: CreatePurchaseOrderInput) =>
		apiFetch<PharmacyPurchaseOrderApi>("/clinical/pharmacy/purchase-orders", {
			method: "POST",
			body: input,
		}),
	orderPharmacyPurchaseOrder: (id: string) =>
		apiFetch<PharmacyPurchaseOrderApi>(
			`/clinical/pharmacy/purchase-orders/${id}/order`,
			{ method: "POST" },
		),
	receivePharmacyPurchaseOrder: (id: string) =>
		apiFetch<PharmacyPurchaseOrderApi>(
			`/clinical/pharmacy/purchase-orders/${id}/receive`,
			{ method: "POST" },
		),
	cancelPharmacyPurchaseOrder: (id: string) =>
		apiFetch<PharmacyPurchaseOrderApi>(
			`/clinical/pharmacy/purchase-orders/${id}/cancel`,
			{ method: "POST" },
		),

	// Clinic consumable supplies (inventory)
	listSupplies: () => apiFetch<ClinicSupplyApi[]>("/clinical/supplies"),
	suppliesLowStock: () =>
		apiFetch<ClinicSupplyApi[]>("/clinical/supplies/low-stock"),
	suppliesExpiring: () =>
		apiFetch<ClinicSupplyApi[]>("/clinical/supplies/expiring"),
	createSupply: (input: CreateClinicSupplyInput) =>
		apiFetch<ClinicSupplyApi>("/clinical/supplies", {
			method: "POST",
			body: input,
		}),
	updateSupply: (id: string, input: UpdateClinicSupplyInput) =>
		apiFetch<ClinicSupplyApi>(`/clinical/supplies/${id}`, {
			method: "PATCH",
			body: input,
		}),
	deleteSupply: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/clinical/supplies/${id}`, {
			method: "DELETE",
		}),
	adjustSupply: (input: AdjustClinicSupplyInput) =>
		apiFetch<ClinicSupplyApi>("/clinical/supplies/adjust", {
			method: "POST",
			body: input,
		}),
};

/**
 * Injectables (botox / filler) — product lots (inventory + recall) and the
 * per-patient injection records. Mounted on the clinical router so it inherits
 * requireAuth + the clinical access guard (GET = read, writes = clinical.manage).
 */
export const injectables = {
	listLots: () => apiFetch<InjectableLotApi[]>("/clinical/injectables/lots"),

	createLot: (input: CreateInjectableLotInput) =>
		apiFetch<InjectableLotApi>("/clinical/injectables/lots", {
			method: "POST",
			body: input,
		}),

	updateLot: (id: string, input: UpdateInjectableLotInput) =>
		apiFetch<InjectableLotApi>(`/clinical/injectables/lots/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteLot: async (id: string): Promise<void> => {
		await apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/injectables/lots/${id}`,
			{ method: "DELETE" },
		);
	},

	listInjections: (patientId: string) =>
		apiFetch<InjectionRecordApi[]>(
			`/clinical/injectables/injections?patient_id=${encodeURIComponent(patientId)}`,
		),

	recordInjection: (input: CreateInjectionRecordInput) =>
		apiFetch<InjectionRecordApi>("/clinical/injectables/injections", {
			method: "POST",
			body: input,
		}),

	deleteInjection: async (id: string): Promise<void> => {
		await apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/injectables/injections/${id}`,
			{ method: "DELETE" },
		);
	},

	recall: (lotId: string) =>
		apiFetch<InjectableRecallApi>(`/clinical/injectables/recall/${lotId}`),
};

/**
 * AI skin analysis — score a single facial photo for aesthetic concerns
 * (acne / pigmentation / pores / wrinkles…) via the Claude vision integration.
 * Assistive screening aid only; results are stored per patient for trending.
 */
export const skinAnalyses = {
	list: (patientId: string): Promise<SkinAnalysisApi[]> =>
		apiFetch<SkinAnalysisApi[]>(
			`/clinical/skin-analyses?patient_id=${encodeURIComponent(patientId)}`,
		),

	analyze: (input: AnalyzeSkinInput): Promise<SkinAnalysisApi> =>
		apiFetch<SkinAnalysisApi>("/clinical/skin-analyses/analyze", {
			method: "POST",
			body: input,
		}),

	delete: async (id: string): Promise<void> => {
		await apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/skin-analyses/${id}`,
			{ method: "DELETE" },
		);
	},
};

/**
 * AI aesthetic treatment planner — generate a phased cosmetic/derma plan from
 * the patient's aesthetic picture (skin analysis, lesions, injectable & laser
 * history) plus the clinician's stated goals. Assistive draft only.
 */
export const aestheticPlans = {
	list: (patientId: string): Promise<AestheticPlanApi[]> =>
		apiFetch<AestheticPlanApi[]>(
			`/clinical/aesthetic-plans?patient_id=${encodeURIComponent(patientId)}`,
		),

	generate: (input: GenerateAestheticPlanInput): Promise<AestheticPlanApi> =>
		apiFetch<AestheticPlanApi>("/clinical/aesthetic-plans/generate", {
			method: "POST",
			body: input,
		}),

	delete: async (id: string): Promise<void> => {
		await apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/aesthetic-plans/${id}`,
			{ method: "DELETE" },
		);
	},
};

/**
 * Photo follow-ups — "send a progress photo in N days" reminders after an
 * aesthetic session. `listDue` is the clinic worklist (due today, or the next 7
 * days with `upcoming`); the rest are per-patient management.
 */
export const photoFollowups = {
	listByPatient: (patientId: string) =>
		apiFetch<PhotoFollowupApi[]>(
			`/clinical/photo-followups?patient_id=${encodeURIComponent(patientId)}`,
		),

	listDue: (upcoming?: boolean) =>
		apiFetch<PhotoFollowupApi[]>(
			`/clinical/photo-followups/due${upcoming ? "?upcoming=1" : ""}`,
		),

	create: (input: CreatePhotoFollowupInput) =>
		apiFetch<PhotoFollowupApi>("/clinical/photo-followups", {
			method: "POST",
			body: input,
		}),

	update: (id: string, input: UpdatePhotoFollowupInput) =>
		apiFetch<PhotoFollowupApi>(`/clinical/photo-followups/${id}`, {
			method: "PATCH",
			body: input,
		}),

	remind: (id: string) =>
		apiFetch<PhotoFollowupApi>(`/clinical/photo-followups/${id}/remind`, {
			method: "POST",
		}),
};

/**
 * Loyalty (points programme) — per-clinic settings + a per-patient points
 * ledger. Mounted on the clinical router so it inherits requireAuth + the
 * clinical access guard (GET = read, writes = clinical.manage).
 */
export const loyalty = {
	getSettings: () => apiFetch<LoyaltySettingsApi>("/clinical/loyalty/settings"),

	updateSettings: (input: UpdateLoyaltySettingsInput) =>
		apiFetch<LoyaltySettingsApi>("/clinical/loyalty/settings", {
			method: "PATCH",
			body: input,
		}),

	summary: (patientId: string) =>
		apiFetch<LoyaltySummaryApi>(
			`/clinical/loyalty/summary?patient_id=${encodeURIComponent(patientId)}`,
		),

	earn: (input: EarnPointsInput) =>
		apiFetch<LoyaltyTransactionApi>("/clinical/loyalty/earn", {
			method: "POST",
			body: input,
		}),

	redeem: (input: RedeemPointsInput) =>
		apiFetch<LoyaltyTransactionApi>("/clinical/loyalty/redeem", {
			method: "POST",
			body: input,
		}),

	adjust: (input: AdjustPointsInput) =>
		apiFetch<LoyaltyTransactionApi>("/clinical/loyalty/adjust", {
			method: "POST",
			body: input,
		}),
};

export const bodyDrawings = {
	listByPatient: (patientId: string) =>
		apiFetch<BodyDrawingApi[]>(
			`/clinical/body-drawings?patient_id=${encodeURIComponent(patientId)}`,
		),

	create: (input: CreateBodyDrawingInput) =>
		apiFetch<BodyDrawingApi>("/clinical/body-drawings", {
			method: "POST",
			body: input,
		}),

	update: (id: string, input: UpdateBodyDrawingInput) =>
		apiFetch<BodyDrawingApi>(`/clinical/body-drawings/${id}`, {
			method: "PATCH",
			body: input,
		}),

	delete: async (id: string): Promise<void> => {
		await apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/body-drawings/${id}`,
			{ method: "DELETE" },
		);
	},
};

/**
 * Treatment plan maps — non-generative aesthetic planning. The clinician pins
 * planned procedures onto a generic mannequin (never the patient's likeness).
 */
export const treatmentPlanMaps = {
	listByPatient: (patientId: string) =>
		apiFetch<TreatmentPlanMapApi[]>(
			`/clinical/treatment-plan-maps?patient_id=${encodeURIComponent(patientId)}`,
		),

	create: (input: CreateTreatmentPlanMapInput) =>
		apiFetch<TreatmentPlanMapApi>("/clinical/treatment-plan-maps", {
			method: "POST",
			body: input,
		}),

	update: (id: string, input: UpdateTreatmentPlanMapInput) =>
		apiFetch<TreatmentPlanMapApi>(`/clinical/treatment-plan-maps/${id}`, {
			method: "PATCH",
			body: input,
		}),

	delete: async (id: string): Promise<void> => {
		await apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/treatment-plan-maps/${id}`,
			{ method: "DELETE" },
		);
	},
};

export const publicLab = {
	get: (token: string) =>
		apiFetch<PublicLabOrderApi>(`/lab/${encodeURIComponent(token)}`, {
			auth: false,
		}),
};

export const publicRadiology = {
	get: (token: string) =>
		apiFetch<PublicRadiologyReportApi>(`/rad/${encodeURIComponent(token)}`, {
			auth: false,
		}),
};

export const billing = {
	createInvoice: (input: CreateInvoiceInput) =>
		apiFetch<InvoiceApi>("/billing/invoices", { method: "POST", body: input }),

	getInvoice: (id: string) => apiFetch<InvoiceApi>(`/billing/invoices/${id}`),

	listInvoices: (query: Partial<InvoicesListQuery>) => {
		const q = new URLSearchParams();
		if (query.status) q.set("status", query.status);
		if (query.patient_id) q.set("patient_id", query.patient_id);
		if (query.from) q.set("from", query.from);
		if (query.to) q.set("to", query.to);
		if (query.page) q.set("page", String(query.page));
		if (query.per_page) q.set("per_page", String(query.per_page));
		return apiFetch<{
			items: InvoiceApi[];
			total: number;
			page: number;
			per_page: number;
		}>(`/billing/invoices?${q}`);
	},

	exportInvoicesCsv: (
		query: Pick<Partial<InvoicesListQuery>, "status" | "from" | "to">,
	) => {
		const q = new URLSearchParams();
		if (query.status) q.set("status", query.status);
		if (query.from) q.set("from", query.from);
		if (query.to) q.set("to", query.to);
		const s = q.toString();
		return apiDownload(
			`/billing/invoices/export${s ? `?${s}` : ""}`,
			`invoices-${new Date().toISOString().slice(0, 10)}.csv`,
		);
	},

	cancelInvoice: (id: string, input: CancelInvoiceInput) =>
		apiFetch<InvoiceApi>(`/billing/invoices/${id}/cancel`, {
			method: "POST",
			body: input,
		}),

	recordPayment: (id: string, input: RecordPaymentInput) =>
		apiFetch<{ invoice: InvoiceApi; payment: PaymentApi }>(
			`/billing/invoices/${id}/payments`,
			{ method: "POST", body: input },
		),

	refundPayment: (id: string, paymentId: string, input: RefundPaymentInput) =>
		apiFetch<{ invoice: InvoiceApi; refund: PaymentApi }>(
			`/billing/invoices/${id}/payments/${paymentId}/refund`,
			{ method: "POST", body: input },
		),

	listInvoicesForPatient: (patientId: string) =>
		apiFetch<InvoiceApi[]>(`/billing/patients/${patientId}/invoices`),

	listInvoicesForVisit: (visitId: string) =>
		apiFetch<InvoiceApi[]>(`/billing/visits/${visitId}/invoices`),

	revenueSummary: () => apiFetch<RevenueSummary>("/billing/revenue/summary"),

	commissionSummary: (q: CommissionRangeQuery) => {
		const p = new URLSearchParams({ from: q.from, to: q.to });
		return apiFetch<CommissionSummary>(`/billing/commission/summary?${p}`);
	},

	commissionDoctor: (memberId: string, q: CommissionRangeQuery) => {
		const p = new URLSearchParams({ from: q.from, to: q.to });
		return apiFetch<CommissionDetail>(
			`/billing/commission/doctor/${encodeURIComponent(memberId)}?${p}`,
		);
	},

	exportCommissionCsv: (q: CommissionRangeQuery) => {
		const p = new URLSearchParams({ from: q.from, to: q.to });
		return apiDownload(
			`/billing/commission/export?${p}`,
			`commissions-${q.from.slice(0, 10)}_${q.to.slice(0, 10)}.csv`,
		);
	},
};

export const search = {
	global: (q: string, limit = 5) => {
		const p = new URLSearchParams();
		p.set("q", q);
		p.set("limit", String(limit));
		return apiFetch<SearchResult>(`/search?${p}`);
	},
};

export const audit = {
	listLogs: (query: Partial<AuditLogsListQuery>) => {
		const q = new URLSearchParams();
		if (query.action) q.set("action", query.action);
		if (query.resource_type) q.set("resource_type", query.resource_type);
		if (query.resource_id) q.set("resource_id", query.resource_id);
		if (query.user_id) q.set("user_id", query.user_id);
		if (query.from) q.set("from", query.from);
		if (query.to) q.set("to", query.to);
		if (query.page) q.set("page", String(query.page));
		if (query.per_page) q.set("per_page", String(query.per_page));
		return apiFetch<AuditLogsListResult>(`/audit/logs?${q}`);
	},
};

export const publicReceipt = {
	get: (token: string) =>
		apiFetch<PublicReceiptApi>(`/receipt/${encodeURIComponent(token)}`, {
			auth: false,
		}),
};

export const publicReviews = {
	info: (slug: string) =>
		apiFetch<ReviewInfo>(
			`/public/clinics/${encodeURIComponent(slug)}/review-info`,
			{ auth: false },
		),
	submit: (slug: string, input: SubmitReviewInput) =>
		apiFetch<SubmitReviewResult>(
			`/public/clinics/${encodeURIComponent(slug)}/reviews`,
			{
				method: "POST",
				body: input,
				auth: false,
			},
		),
};

export const reminders = {
	queue: (days = 2) =>
		apiFetch<ReminderQueueItem[]>(`/reminders/queue?days=${days}`),

	prepare: (appointmentId: string) =>
		apiFetch<PrepareReminderResult>(`/reminders/${appointmentId}/prepare`, {
			method: "POST",
		}),

	markSent: (appointmentId: string) =>
		apiFetch<ReminderQueueItem>(`/reminders/${appointmentId}/mark-sent`, {
			method: "POST",
		}),
};

// ===== Inpatient / HIS (ADT + beds) =====
export const inpatient = {
	listWards: () => apiFetch<WardApi[]>("/inpatient/wards"),
	createWard: (input: CreateWardInput) =>
		apiFetch<WardApi>("/inpatient/wards", { method: "POST", body: input }),
	deleteWard: (id: string) =>
		apiFetch<{ deleted: boolean }>(`/inpatient/wards/${id}`, {
			method: "DELETE",
		}),

	listBeds: (wardId?: string) =>
		apiFetch<BedApi[]>(`/inpatient/beds${wardId ? `?ward_id=${wardId}` : ""}`),
	createBed: (input: CreateBedInput) =>
		apiFetch<BedApi>("/inpatient/beds", { method: "POST", body: input }),
	setBedStatus: (id: string, input: UpdateBedStatusInput) =>
		apiFetch<{ updated: boolean }>(`/inpatient/beds/${id}/status`, {
			method: "PATCH",
			body: input,
		}),
	deleteBed: (id: string) =>
		apiFetch<{ deleted: boolean }>(`/inpatient/beds/${id}`, {
			method: "DELETE",
		}),

	listAdmissions: (status: "active" | "discharged" = "active") =>
		apiFetch<AdmissionApi[]>(`/inpatient/admissions?status=${status}`),
	admit: (input: AdmitPatientInput) =>
		apiFetch<AdmissionApi>("/inpatient/admissions", {
			method: "POST",
			body: input,
		}),
	transfer: (id: string, input: TransferAdmissionInput) =>
		apiFetch<AdmissionApi>(`/inpatient/admissions/${id}/transfer`, {
			method: "PATCH",
			body: input,
		}),
	discharge: (id: string, input: DischargeAdmissionInput) =>
		apiFetch<AdmissionApi>(`/inpatient/admissions/${id}/discharge`, {
			method: "POST",
			body: input,
		}),

	// Phase 2 — nursing
	listNotes: (admissionId: string) =>
		apiFetch<NursingNoteApi[]>(`/inpatient/admissions/${admissionId}/notes`),
	addNote: (input: CreateNursingNoteInput) =>
		apiFetch<NursingNoteApi>("/inpatient/notes", {
			method: "POST",
			body: input,
		}),
	deleteNote: (id: string) =>
		apiFetch<{ deleted: boolean }>(`/inpatient/notes/${id}`, {
			method: "DELETE",
		}),

	listVitals: (admissionId: string) =>
		apiFetch<InpatientVitalApi[]>(
			`/inpatient/admissions/${admissionId}/vitals`,
		),
	addVital: (input: CreateInpatientVitalInput) =>
		apiFetch<InpatientVitalApi>("/inpatient/vitals", {
			method: "POST",
			body: input,
		}),

	listIntakeOutput: (admissionId: string) =>
		apiFetch<IntakeOutputList>(
			`/inpatient/admissions/${admissionId}/intake-output`,
		),
	addIntakeOutput: (input: CreateIntakeOutputInput) =>
		apiFetch<IntakeOutputApi>("/inpatient/intake-output", {
			method: "POST",
			body: input,
		}),
	deleteIntakeOutput: (id: string) =>
		apiFetch<{ deleted: boolean }>(`/inpatient/intake-output/${id}`, {
			method: "DELETE",
		}),

	// Phase 3 — eMAR
	listMedicationOrders: (admissionId: string) =>
		apiFetch<MedicationOrderApi[]>(
			`/inpatient/admissions/${admissionId}/medication-orders`,
		),
	createMedicationOrder: (input: CreateMedicationOrderInput) =>
		apiFetch<MedicationOrderApi>("/inpatient/medication-orders", {
			method: "POST",
			body: input,
		}),
	discontinueMedicationOrder: (id: string) =>
		apiFetch<MedicationOrderApi>(
			`/inpatient/medication-orders/${id}/discontinue`,
			{ method: "POST", body: {} },
		),
	recordAdministration: (input: RecordAdministrationInput) =>
		apiFetch<MedicationAdministrationApi>(
			"/inpatient/medication-administrations",
			{ method: "POST", body: input },
		),

	// Phase 4 — CPOE
	listOrders: (admissionId: string) =>
		apiFetch<PhysicianOrderApi[]>(
			`/inpatient/admissions/${admissionId}/orders`,
		),
	createOrder: (input: CreatePhysicianOrderInput) =>
		apiFetch<PhysicianOrderApi>("/inpatient/orders", {
			method: "POST",
			body: input,
		}),
	completeOrder: (id: string) =>
		apiFetch<PhysicianOrderApi>(`/inpatient/orders/${id}/complete`, {
			method: "POST",
			body: {},
		}),
	cancelOrder: (id: string) =>
		apiFetch<PhysicianOrderApi>(`/inpatient/orders/${id}/cancel`, {
			method: "POST",
			body: {},
		}),

	// Phase 5 — Operating Theatre
	listOperatingRooms: () =>
		apiFetch<OperatingRoomApi[]>("/inpatient/operating-rooms"),
	createOperatingRoom: (input: CreateOperatingRoomInput) =>
		apiFetch<OperatingRoomApi>("/inpatient/operating-rooms", {
			method: "POST",
			body: input,
		}),
	deleteOperatingRoom: (id: string) =>
		apiFetch<{ deleted: boolean }>(`/inpatient/operating-rooms/${id}`, {
			method: "DELETE",
		}),
	listSurgeries: (status?: string) =>
		apiFetch<SurgeryApi[]>(
			`/inpatient/surgeries${status ? `?status=${status}` : ""}`,
		),
	scheduleSurgery: (input: ScheduleSurgeryInput) =>
		apiFetch<SurgeryApi>("/inpatient/surgeries", {
			method: "POST",
			body: input,
		}),
	updateSurgeryStatus: (id: string, input: UpdateSurgeryStatusInput) =>
		apiFetch<SurgeryApi>(`/inpatient/surgeries/${id}/status`, {
			method: "PATCH",
			body: input,
		}),

	// Phase 6 — Emergency Department
	listEdVisits: (status?: string) =>
		apiFetch<EdVisitApi[]>(
			`/inpatient/ed-visits${status ? `?status=${status}` : ""}`,
		),
	registerEdVisit: (input: RegisterEdVisitInput) =>
		apiFetch<EdVisitApi>("/inpatient/ed-visits", {
			method: "POST",
			body: input,
		}),
	updateEdTriage: (id: string, input: UpdateEdTriageInput) =>
		apiFetch<EdVisitApi>(`/inpatient/ed-visits/${id}/triage`, {
			method: "PATCH",
			body: input,
		}),
	updateEdStatus: (id: string, input: UpdateEdStatusInput) =>
		apiFetch<EdVisitApi>(`/inpatient/ed-visits/${id}/status`, {
			method: "PATCH",
			body: input,
		}),

	// Phase 7 — inpatient billing
	getAdmissionBill: (admissionId: string) =>
		apiFetch<AdmissionBill>(`/inpatient/admissions/${admissionId}/bill`),
	addAdmissionCharge: (input: AddAdmissionChargeInput) =>
		apiFetch<AdmissionChargeApi>("/inpatient/charges", {
			method: "POST",
			body: input,
		}),
	deleteAdmissionCharge: (id: string) =>
		apiFetch<{ deleted: boolean }>(`/inpatient/charges/${id}`, {
			method: "DELETE",
		}),
};

export const staff = {
	listMembers: () => apiFetch<ClinicMemberApi[]>("/staff/members"),

	updateMember: (id: string, input: UpdateMemberInput) =>
		apiFetch<ClinicMemberApi>(`/staff/members/${id}`, {
			method: "PATCH",
			body: input,
		}),

	resetMemberPassword: (id: string, newPassword: string) =>
		apiFetch<ClinicMemberApi>(`/staff/members/${id}/reset-password`, {
			method: "POST",
			body: { new_password: newPassword },
		}),

	listInvitations: () => apiFetch<InvitationApi[]>("/staff/invitations"),

	createInvitation: (input: CreateInvitationInput) =>
		apiFetch<InvitationApi>("/staff/invitations", {
			method: "POST",
			body: input,
		}),

	revokeInvitation: (id: string) =>
		apiFetch<{ id: string; revoked: boolean }>(`/staff/invitations/${id}`, {
			method: "DELETE",
		}),

	getMemberPermissions: (memberId: string) =>
		apiFetch<MemberPermissionsApi>(`/staff/members/${memberId}/permissions`, {
			method: "GET",
		}),

	updateMemberPermissions: (memberId: string, input: UpdatePermissionsInput) =>
		apiFetch<MemberPermissionsApi>(`/staff/members/${memberId}/permissions`, {
			method: "PUT",
			body: input,
		}),
};

export const publicInvite = {
	get: (token: string) =>
		apiFetch<PublicInvitationApi>(`/invite/${encodeURIComponent(token)}`, {
			auth: false,
		}),
	accept: (token: string, input: AcceptInvitationInput) =>
		apiFetch<AuthResponse>(`/invite/${encodeURIComponent(token)}/accept`, {
			method: "POST",
			body: input,
			auth: false,
		}),
};

export const photos = {
	status: () => apiFetch<{ enabled: boolean }>("/photos/status"),

	requestUpload: (input: RequestPhotoUploadInput) =>
		apiFetch<PresignedUploadResponse>("/photos/upload-url", {
			method: "POST",
			body: input,
		}),

	confirm: (photoId: string, input: ConfirmPhotoUploadInput) =>
		apiFetch<PatientPhotoApi>(`/photos/${photoId}/confirm`, {
			method: "POST",
			body: input,
		}),

	viewUrl: (photoId: string) =>
		apiFetch<PresignedViewResponse>(`/photos/${photoId}/view-url`),

	list: (patientId: string) =>
		apiFetch<PatientPhotoApi[]>(`/photos/patients/${patientId}`),

	listForLesion: (lesionId: string) =>
		apiFetch<PatientPhotoApi[]>(`/photos/lesions/${lesionId}`),

	update: (photoId: string, input: UpdatePhotoInput) =>
		apiFetch<PatientPhotoApi>(`/photos/${photoId}`, {
			method: "PATCH",
			body: input,
		}),

	remove: (photoId: string) =>
		apiFetch<{ id: string; deleted: boolean }>(`/photos/${photoId}`, {
			method: "DELETE",
		}),
};

export const documents = {
	list: (patientId: string, category?: string) => {
		const q = new URLSearchParams();
		if (category) q.set("category", category);
		const qs = q.toString();
		return apiFetch<PatientDocumentApi[]>(
			`/clinical/patients/${patientId}/documents${qs ? `?${qs}` : ""}`,
		);
	},

	requestUpload: (patientId: string, input: RequestDocumentUploadInput) =>
		apiFetch<PresignedUploadResponse>(
			`/clinical/patients/${patientId}/documents/upload-url`,
			{ method: "POST", body: input },
		),

	confirm: (documentId: string) =>
		apiFetch<PatientDocumentApi>(`/clinical/documents/${documentId}/confirm`, {
			method: "POST",
		}),

	update: (documentId: string, input: UpdateDocumentInput) =>
		apiFetch<PatientDocumentApi>(`/clinical/documents/${documentId}`, {
			method: "PATCH",
			body: input,
		}),

	remove: (documentId: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/documents/${documentId}`,
			{
				method: "DELETE",
			},
		),
};

// ===== Patient portal =====
// Separate fetch helper so the patient JWT (kept in usePortalStore) doesn't
// collide with the staff JWT. Reuses the same /api/v1 base and same
// envelope semantics, but auths via a different localStorage key.
import { usePortalStore } from "../stores/portal-store.ts";

async function portalFetch<T>(
	path: string,
	options: RequestOptions = {},
): Promise<T> {
	const { body, headers = {}, ...rest } = options;
	const token = usePortalStore.getState().token;
	const finalHeaders: Record<string, string> = {
		"Content-Type": "application/json",
		...(token ? { Authorization: `Bearer ${token}` } : {}),
		...headers,
	};
	const res = await fetch(`${API_URL}${path}`, {
		...rest,
		headers: finalHeaders,
		body: body !== undefined ? JSON.stringify(body) : undefined,
	});
	const json = await res.json().catch(() => null);
	if (!res.ok || !json?.ok) {
		throw new HttpError(
			res.status,
			json?.error ?? {
				code: "NETWORK_ERROR",
				message: `Request failed with status ${res.status}`,
			},
		);
	}
	return json.data as T;
}

export const portal = {
	// No auth header — token is in the body
	auth: (token: string) =>
		apiFetch<PortalAuthResponse>("/portal/auth", {
			method: "POST",
			body: { token },
			auth: false,
		}),

	me: () => portalFetch<PortalPatientApi>("/portal/me"),
	appointments: () => portalFetch<Appointment[]>("/portal/appointments"),
	prescriptions: () => portalFetch<PrescriptionApi[]>("/portal/prescriptions"),
	photos: () =>
		portalFetch<Array<PatientPhotoApi & { view_url: string | null }>>(
			"/portal/photos",
		),
	labResults: () => portalFetch<PortalLabResult[]>("/portal/lab-results"),
	radiology: () => portalFetch<PortalRadiologyReport[]>("/portal/radiology"),
	services: () => portalFetch<PortalServiceOption[]>("/portal/services"),
	availableSlots: (params: {
		date: string;
		duration_minutes?: number;
		service_id?: string;
	}) => {
		const query = new URLSearchParams();
		query.set("date", params.date);
		if (params.duration_minutes !== undefined) {
			query.set("duration_minutes", String(params.duration_minutes));
		}
		if (params.service_id) query.set("service_id", params.service_id);
		return portalFetch<AvailableSlotsResponse>(
			`/portal/available-slots?${query}`,
		);
	},
	bookAppointment: (input: PortalBookingInput) =>
		portalFetch<Appointment>("/portal/appointments", {
			method: "POST",
			body: input,
		}),
	cancelAppointment: (id: string) =>
		portalFetch<void>(`/portal/appointments/${id}/cancel`, { method: "POST" }),
};

// Staff side — generate the link
export const portalStaff = {
	generateAccessLink: (patientId: string) =>
		apiFetch<GenerateAccessTokenResponse>(
			`/portal-staff/patients/${patientId}/access-link`,
			{
				method: "POST",
				body: {},
			},
		),
};

export const publicClinic = {
	get: (slug: string) =>
		apiFetch<PublicClinicInfo>(`/public/clinics/${slug}`, { auth: false }),
	services: (slug: string) =>
		apiFetch<PublicClinicService[]>(`/public/clinics/${slug}/services`, {
			auth: false,
		}),
	availableSlots: (
		slug: string,
		params: { date: string; duration_minutes?: number; service_id?: string },
	) => {
		const q = new URLSearchParams();
		q.set("date", params.date);
		if (params.duration_minutes !== undefined)
			q.set("duration_minutes", String(params.duration_minutes));
		if (params.service_id) q.set("service_id", params.service_id);
		return apiFetch<AvailableSlotsResponse>(
			`/public/clinics/${slug}/available-slots?${q}`,
			{ auth: false },
		);
	},
	availabilityDays: (
		slug: string,
		params: {
			from: string;
			days?: number;
			duration_minutes?: number;
			service_id?: string;
		},
	) => {
		const q = new URLSearchParams();
		q.set("from", params.from);
		if (params.days !== undefined) q.set("days", String(params.days));
		if (params.duration_minutes !== undefined)
			q.set("duration_minutes", String(params.duration_minutes));
		if (params.service_id) q.set("service_id", params.service_id);
		return apiFetch<PublicAvailabilityDaysResponse>(
			`/public/clinics/${slug}/availability-days?${q}`,
			{ auth: false },
		);
	},
	book: (slug: string, input: PublicBookingInput) =>
		apiFetch<PublicBookingResult>(`/public/clinics/${slug}/book`, {
			method: "POST",
			body: input,
			auth: false,
		}),
};

export const publicRx = {
	get: (token: string) =>
		apiFetch<PublicPrescriptionApi>(`/rx/${encodeURIComponent(token)}`, {
			auth: false,
		}),
};

export const actions = {
	info: (token: string) =>
		apiFetch<ActionTokenInfo>(`/actions/${token}`, { auth: false }),
	execute: (token: string) =>
		apiFetch<ActionExecuteResult>(`/actions/${token}/execute`, {
			method: "POST",
			body: {},
			auth: false,
		}),
	rescheduleSlots: (token: string, date: string) => {
		const q = new URLSearchParams({ date });
		return apiFetch<AvailableSlotsResponse>(`/actions/${token}/slots?${q}`, {
			auth: false,
		});
	},
	reschedule: (token: string, scheduledAt: string) =>
		apiFetch<ActionExecuteResult>(`/actions/${token}/reschedule`, {
			method: "POST",
			body: { scheduled_at: scheduledAt },
			auth: false,
		}),
};

export const queue = {
	/**
	 * Queue state by slug. Public by default (`auth:false`) → the server returns
	 * the privacy-masked TV payload (masked names, no MRN). Pass `authed:true`
	 * from an in-clinic staff view (the dashboard "current visit" card) to send
	 * the token and receive the full, unmasked payload for the caller's clinic.
	 */
	state: (slug: string, opts?: { authed?: boolean }) =>
		apiFetch<QueueStateResponse>(`/queue/${slug}`, {
			auth: opts?.authed ?? false,
		}),
	advance: () =>
		apiFetch<AdvanceQueueResult>("/queue/advance", {
			method: "POST",
			body: {},
		}),
	bringIn: (appointmentId: string) =>
		apiFetch<BringInPatientResult>("/queue/bring-in", {
			method: "POST",
			body: { appointment_id: appointmentId },
		}),
};

/** Record types whose public /<token> permalink can be revoked / rotated (F2.2). */
export type ShareLinkKind = "prescription" | "lab" | "radiology" | "invoice";

export const shareLinks = {
	/** Kill a (possibly leaked) public link — the permalink then 404s. Terminal. */
	revoke: (kind: ShareLinkKind, id: string) =>
		apiFetch<{ revoked: boolean }>(`/share-links/${kind}/${id}/revoke`, {
			method: "POST",
			body: {},
		}),
	/** Mint a fresh token (clears any revocation); returns the new share_token. */
	rotate: (kind: ShareLinkKind, id: string) =>
		apiFetch<{ share_token: string }>(`/share-links/${kind}/${id}/rotate`, {
			method: "POST",
			body: {},
		}),
};

export const analytics = {
	clinic: (rangeDays: number) =>
		apiFetch<AnalyticsResponse>(`/analytics/clinic?range_days=${rangeDays}`),
	insights: (rangeDays: number) =>
		apiFetch<InsightsResponse>(`/analytics/insights?range_days=${rangeDays}`),
	diagnoses: (rangeDays: number) =>
		apiFetch<DiagnosisReportResponse>(
			`/analytics/diagnoses?range_days=${rangeDays}`,
		),
	practice: (rangeDays: number) =>
		apiFetch<PracticeIntelligenceResponse>(
			`/analytics/practice?range_days=${rangeDays}`,
		),
	cohortLocations: () =>
		apiFetch<CohortLocationsResponse>("/analytics/cohort/locations"),
	cohort: (
		params: { governorate?: string; area?: string; min_sample?: number } = {},
	) => {
		const qs = new URLSearchParams();
		if (params.governorate) qs.set("governorate", params.governorate);
		if (params.area) qs.set("area", params.area);
		if (params.min_sample) qs.set("min_sample", String(params.min_sample));
		const suffix = qs.toString() ? `?${qs.toString()}` : "";
		return apiFetch<CohortInsightResponse>(`/analytics/cohort${suffix}`);
	},
	cohortAsk: (question: string) =>
		apiFetch<CohortAskResult>("/analytics/cohort/ask", {
			method: "POST",
			body: { question },
		}),
	diagnosisEpi: (
		params: { governorate?: string; area?: string; recent_days?: number } = {},
	) => {
		const qs = new URLSearchParams();
		if (params.governorate) qs.set("governorate", params.governorate);
		if (params.area) qs.set("area", params.area);
		if (params.recent_days) qs.set("recent_days", String(params.recent_days));
		const suffix = qs.toString() ? `?${qs.toString()}` : "";
		return apiFetch<DiagnosisEpiResponse>(`/analytics/diagnosis-epi${suffix}`);
	},
};

export const notifications = {
	list: (opts: { unread_only?: boolean; limit?: number } = {}) => {
		const q = new URLSearchParams();
		if (opts.unread_only) q.set("unread_only", "true");
		if (opts.limit) q.set("limit", String(opts.limit));
		const suffix = q.toString() ? `?${q}` : "";
		return apiFetch<NotificationsListResponse>(`/notifications${suffix}`);
	},
	markRead: (id: string) =>
		apiFetch<NotificationApi>(`/notifications/${id}/read`, {
			method: "POST",
			body: {},
		}),
	markAllRead: () =>
		apiFetch<{ marked: number }>("/notifications/mark-all-read", {
			method: "POST",
			body: {},
		}),
	dismiss: (id: string) =>
		apiFetch<{ id: string; dismissed: boolean }>(`/notifications/${id}`, {
			method: "DELETE",
		}),
};

export const assistant = {
	status: () =>
		apiFetch<{
			ai_enabled: boolean;
			stt_enabled: boolean;
			tts_enabled: boolean;
		}>("/voice/status"),

	ask: (
		text: string,
		history?: { role: "user" | "assistant"; content: string }[],
	) =>
		apiFetch<AssistantAskResponse>("/voice/ask", {
			method: "POST",
			body: { text, ...(history ? { history } : {}) },
		}),
};

// ===== Radiology =====

import type {
	ConfirmDicomInstanceInput,
	ConfirmRadiologyImageInput,
	CreateRadiologyOrderInput,
	DicomInstanceApi,
	DicomPresignInput,
	DicomPresignResponse,
	DicomStudyApi,
	FinalizeRadiologyReportInput,
	IncomingRadiologyOrderApi,
	LinkedRadiologyApi,
	ListRadiologyCriticalFindingsQuery,
	MarkRadiologyCriticalFindingNotifiedInput,
	PriorStudyApi,
	PublicRadiologyReportApi,
	RadiologyAssistInput,
	RadiologyAssistResult,
	RadiologyCriticalFindingApi,
	RadiologyDashboardStats,
	RadiologyImageApi,
	RadiologyImagePresignInput,
	RadiologyImagePresignResponse,
	RadiologyModality,
	RadiologyOrderApi,
	RadiologyOrderStatus,
	RadiologyReportAddendumInput,
	RadiologyReportApi,
	RadiologyTatReportQuery,
	RadiologyTatReportResponse,
	RadiologyTemplateApi,
	RejectRadiologyOrderInput,
	UpdateRadiologyOrderStatusInput,
	UpsertRadiologyReportInput,
	UpsertRadiologyTemplateInput,
} from "@klyra/shared";

export interface RadiologyWorklistRow extends RadiologyOrderApi {
	has_report: boolean;
	image_count: number;
}

export const radiology = {
	createOrder: (input: CreateRadiologyOrderInput) =>
		apiFetch<RadiologyOrderApi>("/clinical/radiology-orders", {
			method: "POST",
			body: input,
		}),

	worklist: (status: RadiologyOrderStatus | "all" = "all") =>
		apiFetch<RadiologyWorklistRow[]>(
			`/clinical/radiology-orders/worklist?status=${status}`,
		),

	todayStats: () =>
		apiFetch<RadiologyDashboardStats>("/clinical/radiology-orders/stats"),

	get: (id: string) =>
		apiFetch<RadiologyOrderApi>(`/clinical/radiology-orders/${id}`),

	setStatus: (id: string, input: UpdateRadiologyOrderStatusInput) =>
		apiFetch<RadiologyOrderApi>(`/clinical/radiology-orders/${id}/status`, {
			method: "POST",
			body: input,
		}),

	reject: (id: string, input: RejectRadiologyOrderInput) =>
		apiFetch<RadiologyOrderApi>(`/clinical/radiology-orders/${id}/reject`, {
			method: "POST",
			body: input,
		}),

	listForPatient: (patientId: string) =>
		apiFetch<RadiologyOrderApi[]>(
			`/clinical/patients/${patientId}/radiology-orders`,
		),

	// Report
	getReport: (id: string) =>
		apiFetch<RadiologyReportApi | null>(
			`/clinical/radiology-orders/${id}/report`,
		),

	upsertReport: (id: string, input: UpsertRadiologyReportInput) =>
		apiFetch<RadiologyReportApi>(`/clinical/radiology-orders/${id}/report`, {
			method: "PUT",
			body: input,
		}),

	finalizeReport: (id: string, input: FinalizeRadiologyReportInput) =>
		apiFetch<RadiologyReportApi>(
			`/clinical/radiology-orders/${id}/report/finalize`,
			{ method: "POST", body: input },
		),

	addAddendum: (id: string, input: RadiologyReportAddendumInput) =>
		apiFetch<RadiologyReportApi>(
			`/clinical/radiology-orders/${id}/report/addendum`,
			{ method: "POST", body: input },
		),

	verifyReport: (id: string) =>
		apiFetch<RadiologyReportApi>(
			`/clinical/radiology-orders/${id}/report/verify`,
			{
				method: "POST",
			},
		),

	// Results-inbox acknowledgment by the ordering doctor (idempotent).
	reviewReport: (id: string) =>
		apiFetch<RadiologyReportApi>(
			`/clinical/radiology-orders/${id}/report/review`,
			{
				method: "POST",
			},
		),

	setRecall: (id: string, recallAt: string | null) =>
		apiFetch<RadiologyOrderApi>(`/clinical/radiology-orders/${id}/recall`, {
			method: "POST",
			body: { recall_at: recallAt },
		}),

	priorStudies: (id: string) =>
		apiFetch<PriorStudyApi[]>(`/clinical/radiology-orders/${id}/prior-studies`),

	// AI report assist — drafts impression + recommendations from findings
	assist: (input: RadiologyAssistInput) =>
		apiFetch<
			RadiologyAssistResult & { ai_generated: boolean; disclaimer_ar: string }
		>("/clinical/radiology/assist", { method: "POST", body: input }),

	// Images
	presignImage: (orderId: string, input: RadiologyImagePresignInput) =>
		apiFetch<RadiologyImagePresignResponse>(
			`/clinical/radiology-orders/${orderId}/images/presign`,
			{ method: "POST", body: input },
		),

	confirmImage: (orderId: string, input: ConfirmRadiologyImageInput) =>
		apiFetch<RadiologyImageApi>(
			`/clinical/radiology-orders/${orderId}/images/confirm`,
			{ method: "POST", body: input },
		),

	listImages: (orderId: string) =>
		apiFetch<RadiologyImageApi[]>(
			`/clinical/radiology-orders/${orderId}/images`,
		),

	deleteImage: (imageId: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/radiology-images/${imageId}`,
			{ method: "DELETE" },
		),

	// Critical findings (ISO 15189 §5.8.7 notification log)
	listPendingCriticalFindings: () =>
		apiFetch<RadiologyCriticalFindingApi[]>(
			"/clinical/radiology-critical-findings/pending",
		),

	listCriticalFindings: (
		query: ListRadiologyCriticalFindingsQuery = { limit: 50 },
	) => {
		const qs = new URLSearchParams();
		if (query.status) qs.set("status", query.status);
		if (query.from) qs.set("from", query.from);
		if (query.to) qs.set("to", query.to);
		qs.set("limit", String(query.limit ?? 50));
		return apiFetch<RadiologyCriticalFindingApi[]>(
			`/clinical/radiology-critical-findings?${qs.toString()}`,
		);
	},

	getCriticalFinding: (id: string) =>
		apiFetch<RadiologyCriticalFindingApi>(
			`/clinical/radiology-critical-findings/${id}`,
		),

	markCriticalFindingNotified: (
		id: string,
		input: MarkRadiologyCriticalFindingNotifiedInput,
	) =>
		apiFetch<RadiologyCriticalFindingApi>(
			`/clinical/radiology-critical-findings/${id}/notify`,
			{ method: "PATCH", body: input },
		),

	markCriticalFindingAcknowledged: (id: string) =>
		apiFetch<RadiologyCriticalFindingApi>(
			`/clinical/radiology-critical-findings/${id}/acknowledge`,
			{ method: "PATCH" },
		),

	// Report templates
	listTemplates: (modality?: RadiologyModality) =>
		apiFetch<RadiologyTemplateApi[]>(
			`/clinical/radiology-templates${modality ? `?modality=${modality}` : ""}`,
		),

	createTemplate: (input: UpsertRadiologyTemplateInput) =>
		apiFetch<RadiologyTemplateApi>("/clinical/radiology-templates", {
			method: "POST",
			body: input,
		}),

	updateTemplate: (id: string, input: UpsertRadiologyTemplateInput) =>
		apiFetch<RadiologyTemplateApi>(`/clinical/radiology-templates/${id}`, {
			method: "PATCH",
			body: input,
		}),

	deleteTemplate: (id: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/radiology-templates/${id}`,
			{ method: "DELETE" },
		),

	// TAT report (ISO 15189 §5.6.5)
	tatReport: (query: RadiologyTatReportQuery = {}) => {
		const qs = new URLSearchParams();
		if (query.from) qs.set("from", query.from);
		if (query.to) qs.set("to", query.to);
		const suffix = qs.toString() ? `?${qs.toString()}` : "";
		return apiFetch<RadiologyTatReportResponse>(
			`/clinical/radiology-tat-report${suffix}`,
		);
	},

	// ===== Cross-facility referrals =====

	// Clinic-facing: linked radiology centers (for routing the order).
	linkedCenters: () =>
		apiFetch<LinkedRadiologyApi[]>("/clinical/linked-radiology-centers"),

	// Center-facing: incoming routed studies (cross-tenant, gated).
	incoming: () =>
		apiFetch<IncomingRadiologyOrderApi[]>(
			"/clinical/incoming-radiology-orders",
		),

	incomingReport: (id: string) =>
		apiFetch<RadiologyReportApi | null>(
			`/clinical/incoming-radiology-orders/${id}/report`,
		),

	upsertIncomingReport: (id: string, input: UpsertRadiologyReportInput) =>
		apiFetch<RadiologyReportApi>(
			`/clinical/incoming-radiology-orders/${id}/report`,
			{ method: "PUT", body: input },
		),

	finalizeIncomingReport: (id: string, input: FinalizeRadiologyReportInput) =>
		apiFetch<RadiologyReportApi>(
			`/clinical/incoming-radiology-orders/${id}/report/finalize`,
			{ method: "POST", body: input },
		),

	addIncomingAddendum: (id: string, input: RadiologyReportAddendumInput) =>
		apiFetch<RadiologyReportApi>(
			`/clinical/incoming-radiology-orders/${id}/report/addendum`,
			{ method: "POST", body: input },
		),

	presignIncomingImage: (id: string, input: RadiologyImagePresignInput) =>
		apiFetch<RadiologyImagePresignResponse>(
			`/clinical/incoming-radiology-orders/${id}/images/presign`,
			{ method: "POST", body: input },
		),

	confirmIncomingImage: (id: string, input: ConfirmRadiologyImageInput) =>
		apiFetch<RadiologyImageApi>(
			`/clinical/incoming-radiology-orders/${id}/images/confirm`,
			{ method: "POST", body: input },
		),

	listIncomingImages: (id: string) =>
		apiFetch<RadiologyImageApi[]>(
			`/clinical/incoming-radiology-orders/${id}/images`,
		),

	deleteIncomingImage: (imageId: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/incoming-radiology-images/${imageId}`,
			{ method: "DELETE" },
		),

	deliverIncoming: (id: string) =>
		apiFetch<RadiologyOrderApi>(
			`/clinical/incoming-radiology-orders/${id}/deliver`,
			{ method: "POST" },
		),

	acceptReferral: (id: string, input: AcceptReferralInput = {}) =>
		apiFetch<RadiologyOrderApi>(
			`/clinical/incoming-radiology-orders/${id}/accept`,
			{ method: "POST", body: input },
		),

	rejectIncoming: (id: string, input: RejectRadiologyOrderInput) =>
		apiFetch<RadiologyOrderApi>(
			`/clinical/incoming-radiology-orders/${id}/reject`,
			{ method: "POST", body: input },
		),

	// ===== DICOM / PACS =====

	presignDicom: (orderId: string, input: DicomPresignInput) =>
		apiFetch<DicomPresignResponse>(
			`/clinical/radiology-orders/${orderId}/dicom/presign`,
			{ method: "POST", body: input },
		),
	confirmDicom: (orderId: string, input: ConfirmDicomInstanceInput) =>
		apiFetch<DicomInstanceApi>(
			`/clinical/radiology-orders/${orderId}/dicom/confirm`,
			{ method: "POST", body: input },
		),
	listDicom: (orderId: string) =>
		apiFetch<DicomStudyApi[]>(`/clinical/radiology-orders/${orderId}/dicom`),
	deleteDicomInstance: (instanceId: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/radiology-dicom-instances/${instanceId}`,
			{ method: "DELETE" },
		),

	// Cross-tenant (referred-in study) DICOM.
	presignIncomingDicom: (orderId: string, input: DicomPresignInput) =>
		apiFetch<DicomPresignResponse>(
			`/clinical/incoming-radiology-orders/${orderId}/dicom/presign`,
			{ method: "POST", body: input },
		),
	confirmIncomingDicom: (orderId: string, input: ConfirmDicomInstanceInput) =>
		apiFetch<DicomInstanceApi>(
			`/clinical/incoming-radiology-orders/${orderId}/dicom/confirm`,
			{ method: "POST", body: input },
		),
	listIncomingDicom: (orderId: string) =>
		apiFetch<DicomStudyApi[]>(
			`/clinical/incoming-radiology-orders/${orderId}/dicom`,
		),
	deleteIncomingDicomInstance: (instanceId: string) =>
		apiFetch<{ id: string; deleted: boolean }>(
			`/clinical/incoming-radiology-dicom-instances/${instanceId}`,
			{ method: "DELETE" },
		),
};
